import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Pagination,
    Paper,
    Snackbar,
    Stack,
    Tooltip,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import SettingsIcon from '@mui/icons-material/Settings'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CloseIcon from '@mui/icons-material/Close'
import {
    buildClientReadyActionItems,
    checklistSuggestedStatus,
    checklistFinalStatus,
    buildWordingActionItems
} from './qualityReviewSummary'
import ClientReadyResults from './ClientReadyResults'
import InlineSourceViewer from './InlineSourceViewer'
import ReviewUploadsDialog from './ReviewUploadsDialog'
import { buildReviewUploads, restoreReviewUploads, normalizeUploadName } from './qualityReviewUploads'
import QualityReviewPrivacyDialog from './QualityReviewPrivacyDialog'
import {
    getSourceAccessState,
    isQualityReviewReadOnly,
    isSourceAccessPolicyResolved,
    ManageSourceAccessDialog,
    revokeObjectUrlAfterDownload,
    resolveSourceReviewerOptionsClientId,
    SavedReviewSourceStatus,
    SourceAccessPolicyNotice,
    SourceAccessSummary,
    sourceFileAccessErrorMessage,
    SourceReviewerSetup
} from './QualityReviewSourceAccess'
import {
    MissedIssueDialog,
    QualityScorecardDialog
} from './QualityReviewTrustDialogs'
import {
    assertReviewFilesReadable,
    buildReviewReadiness,
    buildSetupStepVisibility,
    buildSourceConfirmationKey,
    validateReviewFiles
} from './qualityReviewWorkflow'
import {
    clearReviewDraft,
    persistReviewDraft,
    readReviewDraft
} from './qualityReviewPrivacy'
import { filterBizfileSourceWarnings } from './qualityReviewWarnings'
import {
    findSourceNavigationEntry,
    preserveSessionSourceEvidence,
    resolvePdfPageNumber,
    sourceHighlightTexts,
    sourceLocationQuality
} from './qualityReviewSource'

const STATUS_OPTIONS = [
    { value: 'pass', label: 'Pass' },
    { value: 'warning', label: 'Needs Review' },
    { value: 'manual', label: 'Manual' },
    { value: 'fail', label: 'Fail' },
    { value: 'na', label: 'N/A' }
]

const FINDING_FEEDBACK_OPTIONS = [
    { value: 'manual_confirmed', label: 'Correct finding' },
    { value: 'false_positive', label: 'False positive' },
    { value: 'unclear', label: 'Unclear' }
]

function QualityReview() {
    const [clients, setClients] = useState([])
    const [sourceReviewerOptions, setSourceReviewerOptions] = useState([])
    const [sourceRetentionEnabled, setSourceRetentionEnabled] = useState(false)
    const [sourceAccessPolicyStatus, setSourceAccessPolicyStatus] = useState('loading')
    const [sourceAccessPolicyRetry, setSourceAccessPolicyRetry] = useState(0)
    const [loadingSourceReviewers, setLoadingSourceReviewers] = useState(false)
    const [selectedSourceReviewer, setSelectedSourceReviewer] = useState(null)
    const [reviewHistory, setReviewHistory] = useState([])
    const [historySearchTerm, setHistorySearchTerm] = useState('')
    const [uploadedFilesOpen, setUploadedFilesOpen] = useState(false)
    const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false)
    const [preparingRerun, setPreparingRerun] = useState(false)
    const [rerunProgress, setRerunProgress] = useState('')
    const [rerunNotice, setRerunNotice] = useState('')
    const [selectedClient, setSelectedClient] = useState(null)
    const [expectations, setExpectations] = useState({
        expectedCompanyName: '',
        expectedUen: '',
        expectedRegisteredAddress: '',
        expectedFinancialPeriodStart: '',
        expectedFinancialPeriodEnd: '',
        expectedFye: '',
        expectedYa: '',
        expectedTaxSignatory: '',
        expectedDirectors: '',
        expectedCompanySecretaries: '',
        expectedCountryOfDomicile: ''
    })
    const [reviewEvidenceExpectations, setReviewEvidenceExpectations] = useState({})
    const [bizfile, setBizfile] = useState(null)
    const [annualReturn, setAnnualReturn] = useState(null)
    const [bizfileExtraction, setBizfileExtraction] = useState(null)
    const [compilationDocuments, setCompilationDocuments] = useState([])
    const [taxDocuments, setTaxDocuments] = useState([])
    const [otherDocuments, setOtherDocuments] = useState([])
    const [loadingClients, setLoadingClients] = useState(true)
    const [loadingHistory, setLoadingHistory] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [result, setResult] = useState(null)
    const [reviewState, setReviewState] = useState({})
    const [feedbackState, setFeedbackState] = useState({ findings: {}, corrections: {} })
    const [showOptionalSetup, setShowOptionalSetup] = useState(false)
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
    const [resultsViewMode, setResultsViewMode] = useState('checklist')
    const [showTechnicalLabels, setShowTechnicalLabels] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [howChecksDialogOpen, setHowChecksDialogOpen] = useState(false)
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false)
    const [savedReviewsDialogOpen, setSavedReviewsDialogOpen] = useState(false)
    const [newBatchDialogOpen, setNewBatchDialogOpen] = useState(false)
    const [extractingBizfile, setExtractingBizfile] = useState(false)
    const [confirmedSourceKey, setConfirmedSourceKey] = useState('')
    const [sourceEditedFields, setSourceEditedFields] = useState([])
    const [reviewStateDirty, setReviewStateDirty] = useState(false)
    const [savingReviewState, setSavingReviewState] = useState(false)
    const [reviewSaveStatus, setReviewSaveStatus] = useState({ state: 'idle', savedAt: '', message: '' })
    const [savingFeedback, setSavingFeedback] = useState(false)
    const [exportingDataset, setExportingDataset] = useState(false)
    const [progressMinimized, setProgressMinimized] = useState(false)
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' })
    const [uploadValidationMessage, setUploadValidationMessage] = useState('')
    const [focusMode] = useState(true)
    const [showReviewerActions] = useState(false)
    const [pendingSourceScroll, setPendingSourceScroll] = useState(false)
    const [pendingStepThreeScroll, setPendingStepThreeScroll] = useState(false)
    const [pendingNewReviewScroll, setPendingNewReviewScroll] = useState(false)
    const [sourceViewer, setSourceViewer] = useState({ open: false, index: -1, source: null })
    const [openingSourceFileId, setOpeningSourceFileId] = useState('')
    const [downloadingSourceFileId, setDownloadingSourceFileId] = useState('')
    const [sourceAccessDialogOpen, setSourceAccessDialogOpen] = useState(false)
    const [savingSourceAccess, setSavingSourceAccess] = useState(false)
    const [missedIssueDialogOpen, setMissedIssueDialogOpen] = useState(false)
    const [savingMissedIssue, setSavingMissedIssue] = useState(false)
    const [scorecardDialogOpen, setScorecardDialogOpen] = useState(false)
    const [loadingScorecard] = useState(false)
    const [qualityScorecard, setQualityScorecard] = useState(null)
    const [runProgress, setRunProgress] = useState({
        active: false,
        mode: 'determinate',
        value: 0,
        label: '',
        hint: ''
    })
    const summaryRef = useRef(null)
    const bizfileStepRef = useRef(null)
    const sourceConfirmationRef = useRef(null)
    const stepThreeRef = useRef(null)
    const staffToolsRef = useRef(null)
    const processingTransitionRef = useRef(null)
    const progressEventSourceRef = useRef(null)
    const reviewSaveTimerRef = useRef(null)
    const reviewLoadGenerationRef = useRef(0)
    const reviewStateRef = useRef(reviewState)
    const historySearchRef = useRef(historySearchTerm)
    historySearchRef.current = historySearchTerm
    const saveReviewStateRef = useRef(null)
    saveReviewStateRef.current = handleSaveReviewState
    const sourceReviewerOptionsClientId = resolveSourceReviewerOptionsClientId(selectedClient, result)

    function showNotice(message, severity = 'success') {
        setToast({ open: true, message, severity })
    }

    function clearProcessingTransition() {
        if (processingTransitionRef.current) {
            window.clearTimeout(processingTransitionRef.current)
            processingTransitionRef.current = null
        }
    }

    function closeProgressEventSource() {
        if (progressEventSourceRef.current) {
            progressEventSourceRef.current.close()
            progressEventSourceRef.current = null
        }
    }

    function scheduleProcessingTransition() {
        clearProcessingTransition()
        processingTransitionRef.current = window.setTimeout(() => {
            setRunProgress(prev => {
                if (!prev?.active) return prev
                if (!String(prev.label || '').startsWith('Uploading files')) return prev
                return {
                    active: true,
                    mode: 'indeterminate',
                    value: Math.max(prev.value || 0, 12),
                    label: 'Processing documents...',
                    hint: 'Waiting for the server to begin the review stages.'
                }
            })
        }, 1500)
    }

    useEffect(() => {
        let active = true

        async function loadClients() {
            try {
                const response = await axios.get('/api/quality_review/client_options')
                if (!active) return
                setClients(Array.isArray(response.data) ? response.data : [])
            } catch (err) {
                if (!active) return
                setError('Failed to load authorised client links.')
            } finally {
                if (active) {
                    setLoadingClients(false)
                }
            }
        }

        loadClients()

        return () => {
            active = false
            clearProcessingTransition()
            closeProgressEventSource()
        }
    }, [])

    useEffect(() => {
        let active = true

        async function loadSourceReviewerOptions() {
            setLoadingSourceReviewers(true)
            setSourceRetentionEnabled(false)
            setSourceAccessPolicyStatus('loading')
            setSourceReviewerOptions([])
            setSelectedSourceReviewer(null)
            try {
                const response = await axios.get('/api/quality_review/source_reviewer_options', {
                    params: sourceReviewerOptionsClientId ? { clientId: sourceReviewerOptionsClientId } : undefined
                })
                if (!active) return
                const retentionEnabled = Boolean(response.data?.retentionEnabled)
                const reviewers = retentionEnabled && Array.isArray(response.data?.reviewers)
                    ? response.data.reviewers
                    : []
                setSourceRetentionEnabled(retentionEnabled)
                setSourceAccessPolicyStatus(retentionEnabled ? 'enabled' : 'disabled')
                setSourceReviewerOptions(reviewers)
                setSelectedSourceReviewer(current => reviewers.find(option => String(option.userId) === String(current?.userId)) || null)
            } catch (err) {
                if (!active) return
                setSourceRetentionEnabled(false)
                setSourceAccessPolicyStatus('error')
                setSourceReviewerOptions([])
                setSelectedSourceReviewer(null)
            } finally {
                if (active) setLoadingSourceReviewers(false)
            }
        }

        loadSourceReviewerOptions()
        return () => {
            active = false
        }
    }, [sourceReviewerOptionsClientId, sourceAccessPolicyRetry])

    useEffect(() => {
        let active = true

        async function loadReviewHistory() {
            setLoadingHistory(true)

            try {
                const params = { limit: 50 }
                if (historySearchTerm.trim()) params.search = historySearchTerm.trim()

                const response = await axios.get('/api/quality_reviews', { params })
                if (!active) return
                setReviewHistory(Array.isArray(response.data) ? response.data : [])
            } catch (err) {
                if (!active) return
                setError('Failed to load saved quality reviews.')
            } finally {
                if (active) {
                    setLoadingHistory(false)
                }
            }
        }

        setLoadingHistory(true)
        const timer = window.setTimeout(loadReviewHistory, historySearchTerm ? 300 : 0)

        return () => {
            active = false
            window.clearTimeout(timer)
        }
    }, [historySearchTerm])

    useEffect(() => {
        if (error) {
            setToast({ open: true, message: error, severity: 'error' })
        }
    }, [error])

    useEffect(() => {
        if (success) {
            setToast({ open: true, message: success, severity: 'success' })
        }
    }, [success])

    useEffect(() => {
        reviewStateRef.current = reviewState
    }, [reviewState])

    const selectedReviewId = result?.reviewId || ''
    const currentBizfileKey = bizfile ? JSON.stringify([fileKey(bizfile), annualReturn ? fileKey(annualReturn) : '']) : ''
    const bizfileExtractionReady = Boolean(bizfile && bizfileExtraction?.fileKey === currentBizfileKey)
    const currentSourceKey = buildSourceConfirmationKey(expectations, currentBizfileKey)
    const sourceInformationConfirmed = Boolean(
        bizfileExtractionReady &&
        confirmedSourceKey &&
        confirmedSourceKey === currentSourceKey
    )
    const financialPeriodReady = Boolean(expectations.expectedFinancialPeriodStart && expectations.expectedFinancialPeriodEnd)
    const financialPeriodInvalid = isDateInputRangeInvalid(expectations.expectedFinancialPeriodStart, expectations.expectedFinancialPeriodEnd)
    const financialPeriodPresetYear = deriveFinancialPeriodPresetYear(expectations)
    const financialPeriodPresets = buildFinancialPeriodPresets(financialPeriodPresetYear)
    const documentCount = compilationDocuments.length + taxDocuments.length + otherDocuments.length
    const localSourceFileNames = [
        ...(bizfile ? [bizfile] : []),
        ...(annualReturn ? [annualReturn] : []),
        ...compilationDocuments,
        ...taxDocuments,
        ...otherDocuments
    ].map(file => file.name)
    const reviewReadiness = buildReviewReadiness({
        hasBizfile: Boolean(bizfile),
        extractionReady: bizfileExtractionReady,
        sourceConfirmed: sourceInformationConfirmed,
        documentCount,
        financialPeriodReady,
        financialPeriodInvalid
    })
    const reviewReady = reviewReadiness.every(item => item.ready)
    const setupStepVisibility = buildSetupStepVisibility({
        extractionReady: bizfileExtractionReady,
        sourceConfirmed: sourceInformationConfirmed
    })
    const activeReviewExpectations = result ? reviewEvidenceExpectations : expectations
    const activeReviewCompanyName = activeReviewExpectations.expectedCompanyName || selectedClient?.clientInfo?.companyName || ''
    const activeSourceAccess = getSourceAccessState(result)
    const reviewReadOnly = isQualityReviewReadOnly(result)
    const reviewActionBusy = submitting || preparingRerun || savingReviewState || savingFeedback || savingSourceAccess || savingMissedIssue
    const uploadedFiles = buildReviewUploads(result, { bizfile: bizfile ? [bizfile] : [], annual_return: annualReturn ? [annualReturn] : [], compilation_fs: compilationDocuments, tax_computation: taxDocuments, other_documents: otherDocuments })
    const sourceAccessPolicyReady = isSourceAccessPolicyResolved(sourceAccessPolicyStatus)
    const privacySourceAccessPolicyStatus = result?.sourceAccess
        ? result.sourceAccess.retentionEnabled ? 'enabled' : 'disabled'
        : sourceAccessPolicyStatus
    const sourceNavigationEntries = useMemo(
        () => buildSourceNavigationEntries(result, reviewState, activeReviewExpectations),
        [result, reviewState, activeReviewExpectations]
    )

    useEffect(() => {
        if (!pendingSourceScroll || !bizfileExtractionReady) return undefined
        const timeoutId = window.setTimeout(() => {
            sourceConfirmationRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
            setPendingSourceScroll(false)
        }, 100)
        return () => window.clearTimeout(timeoutId)
    }, [pendingSourceScroll, bizfileExtractionReady])

    useEffect(() => {
        if (!reviewReadOnly) return
        setFeedbackDialogOpen(false)
        setSourceAccessDialogOpen(false)
        setMissedIssueDialogOpen(false)
    }, [reviewReadOnly])

    useEffect(() => {
        if (!pendingNewReviewScroll || result) return undefined
        const timer = window.setTimeout(() => {
            bizfileStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setPendingNewReviewScroll(false)
        }, 0)
        return () => window.clearTimeout(timer)
    }, [pendingNewReviewScroll, result])

    useEffect(() => {
        if (!pendingStepThreeScroll || !sourceInformationConfirmed) return undefined
        const timeoutId = window.setTimeout(() => {
            stepThreeRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
            setPendingStepThreeScroll(false)
        }, 100)
        return () => window.clearTimeout(timeoutId)
    }, [pendingStepThreeScroll, sourceInformationConfirmed])

    useEffect(() => {
        if (reviewSaveTimerRef.current) {
            window.clearTimeout(reviewSaveTimerRef.current)
            reviewSaveTimerRef.current = null
        }
        if (!selectedReviewId || !reviewStateDirty || savingReviewState || preparingRerun) return undefined

        persistReviewDraft(selectedReviewId, reviewState)
        setReviewSaveStatus({ state: 'pending', savedAt: '', message: 'Saving shortly…' })
        reviewSaveTimerRef.current = window.setTimeout(() => {
            saveReviewStateRef.current?.({ silent: true })
        }, 900)

        return () => {
            if (reviewSaveTimerRef.current) {
                window.clearTimeout(reviewSaveTimerRef.current)
                reviewSaveTimerRef.current = null
            }
        }
    }, [reviewState, reviewStateDirty, savingReviewState, selectedReviewId, preparingRerun])

    useEffect(() => {
        function protectUnsavedProgress(event) {
            if (!reviewStateDirty) return
            event.preventDefault()
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', protectUnsavedProgress)
        return () => window.removeEventListener('beforeunload', protectUnsavedProgress)
    }, [reviewStateDirty])

    useEffect(() => () => { reviewLoadGenerationRef.current += 1 }, [])

    useEffect(() => () => {
        revokeSourceViewerBlob(sourceViewer.source)
    }, [sourceViewer.source])

    function handleClientChange(_, client) {
        setSelectedClient(client)
        setSuccess('')

        if (!client) return

        if (bizfileExtractionReady) {
            showNotice('Client linked. Confirmed BizFile values were kept unchanged.', 'info')
            return
        }

        const directors = buildDirectorPrefill(client)
        const fye = normalizeFye(client.corpSec?.FYE || '')
        const periodEnd = toDateInputValue(fye)

        setExpectations(prev => ({
            ...prev,
            expectedTaxSignatory: '',
            expectedCompanyName: client.clientInfo?.companyName || '',
            expectedUen: client.clientInfo?.roc || '',
            expectedRegisteredAddress: compactAddress(client.clientInfo?.registeredAddress || ''),
            expectedFinancialPeriodEnd: periodEnd || prev.expectedFinancialPeriodEnd,
            expectedFye: periodEnd ? formatDateInputForDocument(periodEnd) : fye,
            expectedYa: periodEnd && (!prev.expectedYa || prev.expectedYa === deriveYaFromDateInput(prev.expectedFinancialPeriodEnd))
                ? deriveYaFromDateInput(periodEnd)
                : prev.expectedYa,
            expectedDirectors: directors,
            expectedCompanySecretaries: prev.expectedCompanySecretaries || '',
            expectedCountryOfDomicile: prev.expectedCountryOfDomicile || ''
        }))
    }

    function handleExpectationChange(field, value) {
        if (bizfileExtractionReady && [
            'expectedCompanyName',
            'expectedUen',
            'expectedRegisteredAddress',
            'expectedDirectors',
            'expectedCompanySecretaries',
            'expectedCountryOfDomicile'
        ].includes(field)) {
            setSourceEditedFields(prev => prev.includes(field) ? prev : [...prev, field])
            setConfirmedSourceKey('')
            setPendingStepThreeScroll(false)
        }

        setExpectations(prev => {
            if (field === 'expectedFinancialPeriodEnd') {
                const previousDerivedYa = deriveYaFromDateInput(prev.expectedFinancialPeriodEnd)
                const nextDerivedYa = deriveYaFromDateInput(value)
                return {
                    ...prev,
                    expectedFinancialPeriodEnd: value,
                    expectedFye: formatDateInputForDocument(value),
                    expectedYa: !prev.expectedYa || prev.expectedYa === previousDerivedYa ? nextDerivedYa : prev.expectedYa
                }
            }

            return { ...prev, [field]: value }
        })
    }

    function handleFinancialPeriodPreset(preset) {
        setExpectations(prev => {
            const previousDerivedYa = deriveYaFromDateInput(prev.expectedFinancialPeriodEnd)
            const nextDerivedYa = deriveYaFromDateInput(preset.end)
            return {
                ...prev,
                expectedFinancialPeriodStart: preset.start,
                expectedFinancialPeriodEnd: preset.end,
                expectedFye: formatDateInputForDocument(preset.end),
                expectedYa: !prev.expectedYa || prev.expectedYa === previousDerivedYa ? nextDerivedYa : prev.expectedYa
            }
        })
    }

    function handleClearManualOverrides() {
        if (bizfileExtractionReady) {
            setSourceEditedFields([
                'expectedCompanyName',
                'expectedUen',
                'expectedRegisteredAddress',
                'expectedDirectors',
                'expectedCompanySecretaries',
                'expectedCountryOfDomicile'
            ])
        }
        setExpectations(prev => ({
            ...prev,
            expectedCompanyName: '',
            expectedUen: '',
            expectedRegisteredAddress: '',
            expectedTaxSignatory: '',
            expectedDirectors: '',
            expectedCompanySecretaries: '',
            expectedCountryOfDomicile: ''
        }))
    }

    function clearBizfileDerivedExpectations() {
        setExpectations(prev => ({
            ...prev,
            expectedCompanyName: '',
            expectedUen: '',
            expectedRegisteredAddress: '',
            expectedTaxSignatory: '',
            expectedDirectors: '',
            expectedCompanySecretaries: '',
            expectedCountryOfDomicile: ''
        }))
    }

    function handleStartNewBatch() {
        if (reviewActionBusy) return
        reviewLoadGenerationRef.current += 1
        if (reviewSaveTimerRef.current) {
            window.clearTimeout(reviewSaveTimerRef.current)
            reviewSaveTimerRef.current = null
        }
        if (selectedReviewId) clearReviewDraft(selectedReviewId)
        revokeSourceViewerBlob(sourceViewer.source)
        setUploadedFilesOpen(false)
        setRerunConfirmOpen(false)
        setRerunNotice('')
        setBizfile(null)
        setAnnualReturn(null)
        setBizfileExtraction(null)
        setConfirmedSourceKey('')
        setSourceEditedFields([])
        setCompilationDocuments([])
        setTaxDocuments([])
        setOtherDocuments([])
        setResult(null)
        setReviewEvidenceExpectations({})
        setSelectedClient(null)
        setExpectations({
            expectedCompanyName: '',
            expectedUen: '',
            expectedRegisteredAddress: '',
            expectedFinancialPeriodStart: '',
            expectedFinancialPeriodEnd: '',
            expectedFye: '',
            expectedYa: '',
            expectedTaxSignatory: '',
            expectedDirectors: '',
            expectedCompanySecretaries: '',
            expectedCountryOfDomicile: ''
        })
        setReviewState({})
        reviewStateRef.current = {}
        setReviewStateDirty(false)
        setReviewSaveStatus({ state: 'idle', savedAt: '', message: '' })
        setFeedbackState({ findings: {}, corrections: {} })
        setFeedbackDialogOpen(false)
        setShowOptionalSetup(false)
        setPendingSourceScroll(false)
        setPendingStepThreeScroll(false)
        setSourceViewer({ open: false, index: -1, source: null })
        setOpeningSourceFileId('')
        setDownloadingSourceFileId('')
        setSourceAccessDialogOpen(false)
        setSelectedSourceReviewer(null)
        setMissedIssueDialogOpen(false)
        setScorecardDialogOpen(false)
        setSavedReviewsDialogOpen(false)
        setNewBatchDialogOpen(false)
        setUploadValidationMessage('')
        setProgressMinimized(false)
        setToast({ open: false, message: '', severity: 'success' })
        setQualityScorecard(null)
        setError('')
        setSuccess('')
        setRunProgress({
            active: false,
            mode: 'determinate',
            value: 0,
            label: '',
            hint: ''
        })
        closeProgressEventSource()
        clearProcessingTransition()
        setPendingNewReviewScroll(true)
    }

    function requestAddFilesAndRerun() {
        if (reviewActionBusy) return
        setUploadedFilesOpen(false)
        if (reviewStateDirty) setRerunConfirmOpen(true)
        else prepareAddFilesAndRerun()
    }

    async function prepareAddFilesAndRerun() {
        if (reviewActionBusy || !result) return
        setRerunConfirmOpen(false)
        if (reviewSaveTimerRef.current) window.clearTimeout(reviewSaveTimerRef.current)
        reviewSaveTimerRef.current = null
        setPreparingRerun(true)
        setError('')
        const generation = ++reviewLoadGenerationRef.current
        const previousReviewId = selectedReviewId
        const previousExpectations = { ...buildReviewExpectationFields(result), ...reviewEvidenceExpectations }
        const canKeepConfirmation = bizfile && bizfileExtractionReady && confirmedSourceKey === buildSourceConfirmationKey(previousExpectations, currentBizfileKey)
        try {
            const groups = await restoreReviewUploads(result, uploadedFiles, async entry => {
                const response = await axios.get(`/api/quality_review/${previousReviewId}/source/${encodeURIComponent(entry.fileId)}`, { params: { download: 0 }, responseType: 'blob' })
                if (generation !== reviewLoadGenerationRef.current) throw new Error('Review changed. Please try again.')
                return new File([response.data], entry.fileName, { type: response.headers?.['content-type'] || response.data?.type || 'application/octet-stream' })
            }, (index, total) => setRerunProgress(`Preparing file ${index} of ${total}…`))
            if (generation !== reviewLoadGenerationRef.current) return
            const nextBizfile = groups.bizfile[0]
            const nextAnnualReturn = groups.annual_return[0] || null
            const nextSourceFileKey = JSON.stringify([fileKey(nextBizfile), nextAnnualReturn ? fileKey(nextAnnualReturn) : ''])
            let nextExtraction = bizfileExtraction
            let nextExpectations = previousExpectations
            if (!canKeepConfirmation) {
                setRerunProgress('Reading the uploaded BizFile…')
                const formData = new FormData()
                // Saved identity values are re-extracted from the actual retained BizFile.
                for (const key of ['expectedFinancialPeriodStart', 'expectedFinancialPeriodEnd', 'expectedFye', 'expectedYa']) formData.append(key, previousExpectations[key] || '')
                formData.append('bizfile', nextBizfile)
                if (nextAnnualReturn) formData.append('annualReturn', nextAnnualReturn)
                const response = await axios.post('/api/quality_review/bizfile/extract', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                nextExtraction = response.data
                nextExpectations = buildReviewExpectationFields(response.data)
                for (const key of ['expectedFinancialPeriodStart', 'expectedFinancialPeriodEnd', 'expectedFye', 'expectedYa', 'expectedTaxSignatory']) nextExpectations[key] = previousExpectations[key] || ''
            }
            if (generation !== reviewLoadGenerationRef.current) return
            if (reviewSaveTimerRef.current) window.clearTimeout(reviewSaveTimerRef.current)
            reviewSaveTimerRef.current = null
            // A new run creates a separate record; the previous saved result is never overwritten.
            if (previousReviewId) clearReviewDraft(previousReviewId)
            revokeSourceViewerBlob(sourceViewer.source)
            setSourceViewer({ open: false, index: -1, source: null })
            setOpeningSourceFileId('')
            setDownloadingSourceFileId('')
            setUploadedFilesOpen(false)
            setResult(null)
            setBizfile(nextBizfile)
            setAnnualReturn(nextAnnualReturn)
            setCompilationDocuments(groups.compilation_fs)
            setTaxDocuments(groups.tax_computation)
            setOtherDocuments(groups.other_documents)
            setExpectations(nextExpectations)
            setReviewEvidenceExpectations({})
            setBizfileExtraction({ ...nextExtraction, fileKey: nextSourceFileKey })
            setConfirmedSourceKey(canKeepConfirmation ? buildSourceConfirmationKey(nextExpectations, nextSourceFileKey) : '')
            if (!canKeepConfirmation) setSourceEditedFields([])
            setReviewState({})
            reviewStateRef.current = {}
            setReviewStateDirty(false)
            setReviewSaveStatus({ state: 'idle', savedAt: '', message: '' })
            setFeedbackState({ findings: {}, corrections: {} })
            setFeedbackDialogOpen(false)
            setMissedIssueDialogOpen(false)
            setSourceAccessDialogOpen(false)
            setSelectedSourceReviewer(null)
            setSavedReviewsDialogOpen(false)
            setUploadValidationMessage('')
            setSuccess('')
            setToast({ open: false, message: '', severity: 'success' })
            setPendingNewReviewScroll(false)
            setPendingSourceScroll(!canKeepConfirmation)
            setPendingStepThreeScroll(Boolean(canKeepConfirmation))
            const hasPeriod = nextExpectations.expectedFinancialPeriodStart && nextExpectations.expectedFinancialPeriodEnd
            setRerunNotice(`Your uploaded files${hasPeriod ? ' and financial period are' : ' are'} ready. ${canKeepConfirmation ? 'Add or replace documents, then run the review again.' : 'Confirm the BizFile details, then add or replace documents.'}${hasPeriod ? '' : ' This older review did not save the period; enter it before running.'} The previous result remains in Past reviews.`)
        } catch (err) {
            if (generation !== reviewLoadGenerationRef.current) return
            setError(err?.response?.status === 403 || err?.response?.status === 410
                ? 'Some original files are no longer accessible. The previous review has been kept. Start a new review and upload the complete batch again.'
                : err?.message || 'Files could not be restored. The previous review has been kept. Please try again.')
        } finally {
            setPreparingRerun(false)
            setRerunProgress('')
        }
    }

    function handleBizfileSelect(files) {
        const validation = validateReviewFiles(files, {
            accept: '.pdf,.doc,.docx',
            existingFiles: [],
            reservedFiles: [annualReturn, ...compilationDocuments, ...taxDocuments, ...otherDocuments].filter(Boolean),
            maxFiles: 1,
            multiple: false
        })
        if (validation.errors.length) {
            setUploadValidationMessage(validation.errors.join(' '))
        }
        setBizfile(validation.accepted[0] || null)
        setBizfileExtraction(null)
        setPendingSourceScroll(false)
        setPendingStepThreeScroll(false)
        setConfirmedSourceKey('')
        setSourceEditedFields([])
        clearBizfileDerivedExpectations()
    }

    function handleClearBizfile() {
        setBizfile(null)
        setBizfileExtraction(null)
        setPendingSourceScroll(false)
        setPendingStepThreeScroll(false)
        setConfirmedSourceKey('')
        setSourceEditedFields([])
        clearBizfileDerivedExpectations()
    }

    function handleAnnualReturnSelect(files) {
        const validation = validateReviewFiles(files, {
            accept: '.pdf,.doc,.docx',
            reservedFiles: [bizfile, ...compilationDocuments, ...taxDocuments, ...otherDocuments].filter(Boolean),
            maxFiles: 1,
            multiple: false
        })
        if (validation.errors.length) {
            setUploadValidationMessage(validation.errors.join(' '))
            return
        }
        setAnnualReturn(validation.accepted[0] || null)
        setBizfileExtraction(null)
        setConfirmedSourceKey('')
        setSourceEditedFields([])
        setPendingSourceScroll(false)
        setPendingStepThreeScroll(false)
        clearBizfileDerivedExpectations()
    }

    function applyExtractedBizfileSource(sourceOfTruth) {
        const nextExpectations = sourceOfTruth?.expectations || {}
        setExpectations(prev => ({
            ...prev,
            expectedCompanyName: nextExpectations.expectedCompanyName || '',
            expectedUen: nextExpectations.expectedUen || '',
            expectedRegisteredAddress: nextExpectations.expectedRegisteredAddress || '',
            expectedFinancialPeriodStart: toDateInputValue(nextExpectations.expectedFinancialPeriodStart || '') || prev.expectedFinancialPeriodStart,
            expectedFinancialPeriodEnd: toDateInputValue(nextExpectations.expectedFinancialPeriodEnd || nextExpectations.expectedFye || '') || prev.expectedFinancialPeriodEnd,
            expectedFye: nextExpectations.expectedFye || prev.expectedFye,
            expectedYa: nextExpectations.expectedYa || prev.expectedYa,
            expectedDirectors: Array.isArray(nextExpectations.expectedDirectors)
                ? nextExpectations.expectedDirectors.join('; ')
                : (nextExpectations.expectedDirectors || ''),
            expectedCompanySecretaries: Array.isArray(nextExpectations.expectedCompanySecretaries)
                ? nextExpectations.expectedCompanySecretaries.join('; ')
                : (nextExpectations.expectedCompanySecretaries || ''),
            expectedCountryOfDomicile: nextExpectations.expectedCountryOfDomicile || ''
        }))
    }

    async function handleExtractBizfile() {
        if (!bizfile) {
            setError('Upload the BizFile before extracting source information.')
            return
        }

        setExtractingBizfile(true)
        setError('')
        setSuccess('')
        setPendingStepThreeScroll(false)

        try {
            const formData = new FormData()
            Object.entries(expectations).forEach(([key, value]) => {
                formData.append(key, value || '')
            })
            if (selectedClient?._id) {
                formData.append('clientId', selectedClient._id)
            }
            formData.append('bizfile', bizfile)
            if (annualReturn) formData.append('annualReturn', annualReturn)

            const response = await axios.post('/api/quality_review/bizfile/extract', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            const extracted = {
                ...response.data,
                fileKey: currentBizfileKey
            }
            setBizfileExtraction(extracted)
            applyExtractedBizfileSource(response.data?.sourceOfTruth)
            setConfirmedSourceKey('')
            setSourceEditedFields([])
            setPendingStepThreeScroll(false)
            setPendingSourceScroll(true)
            setSuccess('BizFile information extracted. Confirm the values before uploading the rest of the batch.')
        } catch (err) {
            setBizfileExtraction(null)
            setError(err?.response?.data?.message || 'Failed to extract BizFile information.')
        } finally {
            setExtractingBizfile(false)
        }
    }

    function handleConfirmSourceInformation() {
        if (!bizfileExtractionReady) {
            setError('Extract the BizFile information before confirming it.')
            return
        }
        setPendingSourceScroll(false)
        setConfirmedSourceKey(currentSourceKey)
        setPendingStepThreeScroll(true)
        setSuccess('')
        showNotice('Source information confirmed. You can run the review when the remaining items are ready.')
    }

    function appendCompilationDocuments(files) {
        setCompilationDocuments(prev => appendValidatedFiles(
            prev,
            files,
            'FS',
            [bizfile, annualReturn, ...taxDocuments, ...otherDocuments].filter(Boolean)
        ))
    }

    function appendTaxDocuments(files) {
        setTaxDocuments(prev => appendValidatedFiles(
            prev,
            files,
            'Tax',
            [bizfile, annualReturn, ...compilationDocuments, ...otherDocuments].filter(Boolean)
        ))
    }

    function appendOtherDocuments(files) {
        setOtherDocuments(prev => appendValidatedFiles(
            prev,
            files,
            'Other',
            [bizfile, annualReturn, ...compilationDocuments, ...taxDocuments].filter(Boolean)
        ))
    }

    function appendValidatedFiles(existingFiles, files, bundleLabel, reservedFiles = []) {
        const validation = validateReviewFiles(files, {
            accept: '.pdf,.doc,.docx,.xlsx,.xls',
            existingFiles,
            reservedFiles,
            maxFiles: 30,
            multiple: true
        })
        if (validation.errors.length) {
            setUploadValidationMessage(`${bundleLabel} files: ${validation.errors.join(' ')}`)
        } else {
            setUploadValidationMessage('')
        }
        return [...existingFiles, ...validation.accepted]
    }

    function handleRemoveBundleFile(bundleKey, targetKey) {
        const remove = list => list.filter(file => fileKey(file) !== targetKey)

        if (bundleKey === 'compilation_fs') {
            setCompilationDocuments(prev => remove(prev))
            return
        }

        if (bundleKey === 'tax_computation') {
            setTaxDocuments(prev => remove(prev))
            return
        }

        setOtherDocuments(prev => remove(prev))
    }

    function handleMoveBundleFile(sourceBundleKey, targetBundleKey, targetKey) {
        const sources = {
            compilation_fs: [compilationDocuments, setCompilationDocuments],
            tax_computation: [taxDocuments, setTaxDocuments],
            other_documents: [otherDocuments, setOtherDocuments]
        }
        const source = sources[sourceBundleKey]
        const target = sources[targetBundleKey]
        if (!source || !target || sourceBundleKey === targetBundleKey) return

        const file = source[0].find(item => fileKey(item) === targetKey)
        if (!file) return
        source[1](prev => prev.filter(item => fileKey(item) !== targetKey))
        target[1](prev => mergeFiles(prev, [file]))
        showNotice(`${file.name} moved to ${humanizeBundleLabel(targetBundleKey)}.`, 'info')
    }

    async function handleRunReview() {
        if (!sourceAccessPolicyReady) {
            setError('Source-file handling must be confirmed before starting the quality check. Retry the policy check.')
            return
        }
        if (!bizfile) {
            setError('Upload the BizFile source-of-truth PDF before running the review.')
            return
        }

        if (!bizfileExtractionReady) {
            setError('Extract and confirm the BizFile source information before running the review.')
            return
        }

        if (!sourceInformationConfirmed) {
            setError('Confirm the BizFile source information before running the review.')
            return
        }

        if (!documentCount) {
            setError('Upload at least one document bundle file to review.')
            return
        }

        if (!financialPeriodReady) {
            setError('Enter the financial period start and end dates before running the review.')
            return
        }

        if (financialPeriodInvalid) {
            setError('Financial period end date must be after the start date.')
            return
        }

        try {
            await assertReviewFilesReadable([
                bizfile,
                annualReturn,
                ...compilationDocuments,
                ...taxDocuments,
                ...otherDocuments
            ])
        } catch (readError) {
            setError(readError.message)
            return
        }

        setSubmitting(true)
        setProgressMinimized(false)
        setError('')
        setSuccess('')
        setRunProgress({
            active: true,
            mode: 'determinate',
            value: 0,
            label: 'Uploading files...',
            hint: 'Sending the BizFile and review bundle to the server.'
        })
        scheduleProcessingTransition()

        try {
            const progressId = createProgressId()
            if (typeof window !== 'undefined' && typeof window.EventSource !== 'undefined') {
                closeProgressEventSource()
                const eventSource = new window.EventSource(`/api/quality_review/progress/${progressId}`)
                progressEventSourceRef.current = eventSource

                eventSource.onmessage = (event) => {
                    try {
                        const payload = JSON.parse(event.data || '{}')
                        if (payload?.type === 'close') {
                            closeProgressEventSource()
                            return
                        }

                        if (payload?.stage === 'error') {
                            setRunProgress({
                                active: true,
                                mode: 'indeterminate',
                                value: 100,
                                label: payload.label || 'Review failed',
                                hint: payload.hint || ''
                            })
                            return
                        }

                        const nextValue = Number.isFinite(Number(payload?.value)) ? Number(payload.value) : 0
                        setRunProgress({
                            active: true,
                            mode: payload?.stage === 'complete' ? 'determinate' : 'indeterminate',
                            value: nextValue,
                            label: payload.label || 'Processing documents...',
                            hint: payload.hint || ''
                        })
                    } catch (parseError) {
                        console.log('quality review progress parse error', parseError)
                    }
                }

                eventSource.onerror = () => {
                    closeProgressEventSource()
                }
            }

            const formData = new FormData()

            Object.entries(expectations).forEach(([key, value]) => {
                formData.append(key, value || '')
            })

            if (selectedClient?._id) {
                formData.append('clientId', selectedClient._id)
            }

            formData.append('progressId', progressId)
            formData.append('sourceConfirmed', 'true')
            formData.append('sourceConfirmationKey', confirmedSourceKey)
            formData.append('sourceEditedFields', JSON.stringify(sourceEditedFields))
            if (sourceRetentionEnabled && selectedSourceReviewer?.userId) {
                formData.append('sourceReviewerUserId', selectedSourceReviewer.userId)
            }
            if (bizfileExtraction?.sourceOfTruth?.confirmation?.extractionFingerprint) {
                formData.append(
                    'extractionFingerprint',
                    bizfileExtraction.sourceOfTruth.confirmation.extractionFingerprint
                )
            }
            formData.append('bizfile', bizfile)
            if (annualReturn) formData.append('annualReturn', annualReturn)
            compilationDocuments.forEach(file => formData.append('compilationDocuments', file))
            taxDocuments.forEach(file => formData.append('taxDocuments', file))
            otherDocuments.forEach(file => formData.append('otherDocuments', file))

            const response = await axios.post('/api/quality_review/review', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const total = progressEvent.total || 0
                    if (!total) {
                        setRunProgress({
                            active: true,
                            mode: 'indeterminate',
                            value: 0,
                            label: 'Uploading files...',
                            hint: 'Sending the BizFile and review bundle to the server.'
                        })
                        scheduleProcessingTransition()
                        return
                    }

                    const percent = Math.round((progressEvent.loaded / total) * 100)

                    if (percent >= 100) {
                        clearProcessingTransition()
                        setRunProgress({
                            active: true,
                            mode: 'indeterminate',
                            value: 100,
                            label: 'Processing documents...',
                            hint: 'Waiting for the server to confirm the current review stage.'
                        })
                        return
                    }

                    setRunProgress({
                        active: true,
                        mode: 'determinate',
                        value: percent,
                        label: `Uploading files... ${percent}%`,
                        hint: 'Sending the BizFile and review bundle to the server.'
                    })
                    scheduleProcessingTransition()
                }
            })

            clearProcessingTransition()
            closeProgressEventSource()
            setRunProgress({
                active: true,
                mode: 'determinate',
                value: 100,
                label: 'Review complete',
                hint: 'The review results are ready.'
            })
            // Keep confirmed values in browser memory for evidence guidance. The
            // privacy-minimised saved result intentionally does not retain them.
            setReviewEvidenceExpectations({ ...expectations })
            applyLoadedReview(response.data)
            setRerunNotice('')
            await refreshReviewHistory()
            scrollToSummary()
        } catch (err) {
            closeProgressEventSource()
            if (err?.response?.status === 413) {
                setError('The uploaded review batch is too large for the production server to accept in one request. Split the files into a smaller batch, or increase the server upload limit.')
            } else if (!err?.response) {
                setError('The browser could not send the selected files. Keep this page open, remove and reselect the files, then try again.')
            } else {
                setError(err?.response?.data?.message || 'Failed to run quality review.')
            }
        } finally {
            clearProcessingTransition()
            closeProgressEventSource()
            setSubmitting(false)
            window.setTimeout(() => {
                setRunProgress({
                    active: false,
                    mode: 'determinate',
                    value: 0,
                    label: '',
                    hint: ''
                })
            }, 500)
        }
    }

    async function handleLoadSavedReview(reviewId) {
        if (preparingRerun) return
        setUploadedFilesOpen(false)
        setRerunNotice('')
        const generation = ++reviewLoadGenerationRef.current
        setOpeningSourceFileId('')
        setDownloadingSourceFileId('')
        setError('')
        setSuccess('')

        try {
            const response = await axios.get(`/api/quality_review/${reviewId}`)
            if (generation !== reviewLoadGenerationRef.current) return
            revokeSourceViewerBlob(sourceViewer.source)
            setSourceViewer({ open: false, index: -1, source: null })
            setBizfile(null)
            setAnnualReturn(null)
            setBizfileExtraction(null)
            setCompilationDocuments([])
            setTaxDocuments([])
            setOtherDocuments([])
            setSelectedClient(null)
            setReviewEvidenceExpectations(buildReviewExpectationFields(response.data))
            applyLoadedReview(response.data)
            setSavedReviewsDialogOpen(false)
            setSuccess('Saved review opened.')
            scrollToSummary()
        } catch (err) {
            if (generation !== reviewLoadGenerationRef.current) return
            setError(err?.response?.data?.message || 'Failed to load saved review.')
        }
    }

    function handleResolutionChange(itemId, resolution, item) {
        if (reviewReadOnly) return
        setReviewState(prev => ({
            ...prev,
            [itemId]: {
                ...(prev?.[itemId] || {}),
                finalStatus: prev?.[itemId]?.finalStatus || item?.finalStatus || item?.suggestedStatus || 'warning',
                resolution
            }
        }))
        setReviewStateDirty(true)
    }

    function handleReviewNoteChange(itemId, note, item) {
        if (reviewReadOnly) return
        setReviewState(prev => ({
            ...prev,
            [itemId]: {
                ...(prev?.[itemId] || {}),
                finalStatus: prev?.[itemId]?.finalStatus || item?.finalStatus || item?.suggestedStatus || 'warning',
                resolution: prev?.[itemId]?.resolution || item?.resolution || 'open',
                note
            }
        }))
        setReviewStateDirty(true)
    }

    async function handleSaveReviewState(options = {}) {
        if (reviewReadOnly || preparingRerun || !selectedReviewId || !reviewStateDirty) return

        const silent = Boolean(options?.silent)
        const generation = reviewLoadGenerationRef.current
        const stateBeingSaved = reviewStateRef.current
        const serializedStateBeingSaved = JSON.stringify(stateBeingSaved)
        setSavingReviewState(true)
        setReviewSaveStatus({ state: 'saving', savedAt: '', message: 'Saving…' })
        try {
            const response = await axios.put(`/api/quality_review/${selectedReviewId}/reviewer_state`, {
                reviewState: stateBeingSaved,
                expectedRevision: result?.revision ?? result?.reviewRevision ?? undefined
            })
            if (generation !== reviewLoadGenerationRef.current) return
            const responseWithSourceEvidence = preserveSessionSourceEvidence(result, response.data)
            const unchangedSinceRequest = JSON.stringify(reviewStateRef.current) === serializedStateBeingSaved
            if (unchangedSinceRequest) {
                applyLoadedReview(responseWithSourceEvidence, { skipDraftRecovery: true, preserveView: true })
                clearReviewDraft(selectedReviewId)
                setReviewStateDirty(false)
            } else {
                setResult({ ...responseWithSourceEvidence, reviewState: reviewStateRef.current })
                setReviewStateDirty(true)
            }
            const savedAt = new Date().toISOString()
            setReviewSaveStatus({ state: 'saved', savedAt, message: `Saved ${formatSavedTime(savedAt)}` })
            if (!silent) showNotice('Review progress saved.')
            await refreshReviewHistory()
        } catch (err) {
            if (generation !== reviewLoadGenerationRef.current) return
            const conflict = err?.response?.status === 409
            const message = conflict
                ? 'Another reviewer updated this review. Reopen it before saving your decisions.'
                : err?.response?.data?.message || 'Failed to save review progress.'
            setReviewSaveStatus({ state: conflict ? 'conflict' : 'error', savedAt: '', message })
            if (!silent || conflict) {
                setError(message)
                showNotice(message, 'error')
            }
        } finally {
            setSavingReviewState(false)
        }
    }

    function handleOpenDocument(fileId, fileName, location, sourceLocation = null, itemId = '') {
        const requested = findSourceNavigationEntry(sourceNavigationEntries, {
            fileId,
            fileName,
            location,
            sourceLocation,
            itemId
        })
        const entry = requested.entry || {
            fileId,
            fileName,
            location,
            itemId,
            label: '',
            ...(sourceLocation || {})
        }
        openSourceNavigationEntry(entry, requested.index)
    }

    async function openSourceNavigationEntry(entry, index) {
        const sourceAccess = getSourceAccessState(result)
        const retainedFileId = String(entry?.fileId || '')
        const localSource = result?.sourceAccess?.retentionEnabled ? null : buildSourceViewerSource({
            entry,
            localFiles: [
                ...(bizfile ? [bizfile] : []),
                ...(annualReturn ? [annualReturn] : []),
                ...compilationDocuments,
                ...taxDocuments,
                ...otherDocuments
            ]
        })

        if (localSource) {
            revokeSourceViewerBlob(sourceViewer.source)
            setSourceViewer({ open: true, index, source: {
                ...localSource,
                accessExpiresAt: result?.sourceAccess?.expiresAt || result?.retention?.sourceExpiresAt || '',
                retained: activeSourceAccess.canOpen && activeSourceAccess.retainedFileIds.includes(retainedFileId)
            } })
            return
        }

        if (!selectedReviewId || !sourceAccess.canOpen || !sourceAccess.retainedFileIds.includes(retainedFileId)) {
            const message = sourceAccess.state === 'deleted' || sourceAccess.state === 'expired' || sourceAccess.state === 'unavailable'
                ? 'This file is no longer stored. The issue location remains available for your working copy.'
                : sourceAccess.visible && !sourceAccess.canAccess
                    ? 'You do not have access to this file. Ask the uploader to grant access.'
                    : 'Open your own working copy and use the location shown.'
            showNotice(message, 'info')
            return
        }

        setOpeningSourceFileId(retainedFileId)
        const generation = reviewLoadGenerationRef.current
        try {
            const response = await axios.get(`/api/quality_review/${selectedReviewId}/source/${encodeURIComponent(retainedFileId)}`, {
                params: { download: 0 },
                responseType: 'blob'
            })
            if (generation !== reviewLoadGenerationRef.current) return
            const contentType = response.headers?.['content-type'] || response.data?.type || 'application/octet-stream'
            const retainedFile = new File([response.data], entry.fileName || 'Source document', { type: contentType })
            const nextSource = buildSourceViewerSource({ entry, sourceFile: retainedFile, localOnly: false })
            if (!nextSource) throw new Error('Unable to prepare source file')
            revokeSourceViewerBlob(sourceViewer.source)
            setSourceViewer({ open: true, index, source: {
                ...nextSource,
                accessExpiresAt: sourceAccess.expiresAt,
                retained: true
            } })
        } catch (err) {
            if (generation !== reviewLoadGenerationRef.current) return
            const code = String(err?.response?.data?.code || '')
            if (err?.response?.status === 403 || code === 'QUALITY_REVIEW_SOURCE_FORBIDDEN') {
                showNotice(sourceFileAccessErrorMessage(403, 'open'), 'error')
            } else if (err?.response?.status === 410 || code === 'QUALITY_REVIEW_SOURCE_EXPIRED' || code === 'QUALITY_REVIEW_SOURCE_UNAVAILABLE') {
                setResult(current => current ? {
                    ...current,
                    sourceAccess: { ...(current.sourceAccess || {}), available: false }
                } : current)
                showNotice(sourceFileAccessErrorMessage(410, 'open'), 'info')
            } else {
                showNotice(sourceFileAccessErrorMessage(err?.response?.status, 'open'), 'error')
            }
        } finally {
            if (generation === reviewLoadGenerationRef.current) setOpeningSourceFileId('')
        }
    }

    function closeSourceViewer() {
        revokeSourceViewerBlob(sourceViewer.source)
        setSourceViewer({ open: false, index: -1, source: null })
    }

    async function handleDownloadSource(source) {
        if (!source?.fullDocumentUrl) return
        const retainedFileId = String(source.fileId || '')
        if (!source.retained || !selectedReviewId || !retainedFileId) {
            downloadFromUrl(source.fullDocumentUrl, source.fileName || 'source-document')
            return
        }

        setDownloadingSourceFileId(retainedFileId)
        const generation = reviewLoadGenerationRef.current
        try {
            const response = await axios.get(`/api/quality_review/${selectedReviewId}/source/${encodeURIComponent(retainedFileId)}`, {
                params: { download: 1 },
                responseType: 'blob'
            })
            if (generation !== reviewLoadGenerationRef.current) return
            const contentType = response.headers?.['content-type'] || response.data?.type || 'application/octet-stream'
            const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType })
            const url = window.URL.createObjectURL(blob)
            downloadFromUrl(url, source.fileName || 'source-document')
            revokeObjectUrlAfterDownload(url)
        } catch (err) {
            if (generation !== reviewLoadGenerationRef.current) return
            if (err?.response?.status === 403) {
                showNotice(sourceFileAccessErrorMessage(403, 'download'), 'error')
            } else if (err?.response?.status === 410) {
                setResult(current => current ? {
                    ...current,
                    sourceAccess: { ...(current.sourceAccess || {}), available: false }
                } : current)
                showNotice(sourceFileAccessErrorMessage(410, 'download'), 'info')
            } else {
                showNotice(sourceFileAccessErrorMessage(err?.response?.status, 'download'), 'error')
            }
        } finally {
            if (generation === reviewLoadGenerationRef.current) setDownloadingSourceFileId('')
        }
    }

    function applySourceAccessUpdate(sourceAccess) {
        if (!sourceAccess) return
        setResult(current => current ? { ...current, sourceAccess } : current)
        setReviewHistory(current => current.map(review => review.reviewId === selectedReviewId
            ? { ...review, sourceAccess }
            : review
        ))
    }

    async function handleSaveSourceAccess(reviewerUserId) {
        if (reviewReadOnly || !selectedReviewId || !reviewerUserId) return
        setSavingSourceAccess(true)
        try {
            const response = await axios.put(`/api/quality_review/${selectedReviewId}/source_access`, { reviewerUserId })
            applySourceAccessUpdate(response.data?.sourceAccess || response.data)
            setSourceAccessDialogOpen(false)
            showNotice('Source file access updated.')
        } catch (err) {
            setError(sourceAccessMutationError(err, 'Failed to update source file access.'))
        } finally {
            setSavingSourceAccess(false)
        }
    }

    async function handleRevokeSourceAccess() {
        if (reviewReadOnly || !selectedReviewId) return
        setSavingSourceAccess(true)
        try {
            const response = await axios.delete(`/api/quality_review/${selectedReviewId}/source_access/reviewer`)
            applySourceAccessUpdate(response.data?.sourceAccess || response.data)
            setSourceAccessDialogOpen(false)
            showNotice('Colleague access revoked.')
        } catch (err) {
            setError(sourceAccessMutationError(err, 'Failed to revoke source file access.'))
        } finally {
            setSavingSourceAccess(false)
        }
    }

    async function handleDeleteSourcesNow() {
        if (reviewReadOnly || !selectedReviewId) return
        setSavingSourceAccess(true)
        try {
            const response = await axios.delete(`/api/quality_review/${selectedReviewId}/sources`)
            applySourceAccessUpdate(response.data?.sourceAccess || response.data)
            revokeSourceViewerBlob(sourceViewer.source)
            setBizfile(null)
            setAnnualReturn(null)
            setCompilationDocuments([])
            setTaxDocuments([])
            setOtherDocuments([])
            setSourceViewer({ open: false, index: -1, source: null })
            setSourceAccessDialogOpen(false)
            showNotice('Source files deleted. The review results and page references remain.', 'info')
        } catch (err) {
            setError(sourceAccessMutationError(err, 'Failed to delete source files.'))
        } finally {
            setSavingSourceAccess(false)
        }
    }

    async function handleReportMissedIssue(payload) {
        if (reviewReadOnly || !selectedReviewId) return
        setSavingMissedIssue(true)
        try {
            const response = await axios.post(`/api/quality_review/${selectedReviewId}/missed_issues`, {
                checklistItemId: payload.checklistItemId,
                fileId: payload.fileId,
                location: parseMissedIssueLocation(payload.location)
            })
            applyLoadedReview(response.data, { skipDraftRecovery: true })
            setMissedIssueDialogOpen(false)
            showNotice('Missed location recorded for the scorecard and UAT triage.')
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to record the missed issue.')
        } finally {
            setSavingMissedIssue(false)
        }
    }

    function handleConfirmCorrectClient(alert) {
        const stateKey = alert.stateKey || alert.id
        handleReviewNoteChange(
            stateKey,
            'Reviewer confirmed that this document belongs to the BizFile company.',
            alert
        )
        handleResolutionChange(stateKey, 'not_applicable', alert)
    }

    function handleFeedbackChange(group, feedbackId, field, value) {
        if (reviewReadOnly) return
        setFeedbackState(prev => ({
            findings: { ...(prev?.findings || {}) },
            corrections: { ...(prev?.corrections || {}) },
            [group]: {
                ...(prev?.[group] || {}),
                [feedbackId]: {
                    ...((prev?.[group] || {})[feedbackId] || {}),
                    [field]: value
                }
            }
        }))
    }

    function handleInlineFindingFeedback(item, decision) {
        if (reviewReadOnly) return
        const feedbackIds = Array.from(new Set((item?.files || [])
            .map(file => `finding:${file.fileId || file.fileName}:${item.id}`)
            .filter(Boolean)))
        if (!feedbackIds.length) return

        const nextFeedbackState = {
            findings: { ...(feedbackState?.findings || {}) },
            corrections: { ...(feedbackState?.corrections || {}) }
        }
        feedbackIds.forEach(feedbackId => {
            nextFeedbackState.findings[feedbackId] = {
                ...(nextFeedbackState.findings[feedbackId] || {}),
                decision
            }
        })
        setFeedbackState(nextFeedbackState)
        saveFeedbackState(nextFeedbackState, { quiet: true })
    }

    function handleSaveFeedback() {
        return saveFeedbackState(feedbackState)
    }

    async function saveFeedbackState(nextFeedbackState, { quiet = false } = {}) {
        if (reviewReadOnly || !selectedReviewId) return

        setSavingFeedback(true)
        setError('')
        setSuccess('')

        try {
            const response = await axios.put(`/api/quality_review/${selectedReviewId}/feedback_state`, {
                feedbackState: nextFeedbackState,
                expectedRevision: result?.revision ?? result?.reviewRevision ?? undefined
            })
            applyLoadedReview(response.data, {
                skipDraftRecovery: true,
                preserveView: true,
                preserveReviewerState: true
            })
            if (!quiet) setSuccess('Reviewer feedback saved.')
        } catch (err) {
            setError(err?.response?.status === 409
                ? 'Another reviewer updated this review. Reopen it before saving feedback.'
                : err?.response?.data?.message || 'Failed to save reviewer feedback.'
            )
        } finally {
            setSavingFeedback(false)
        }
    }

    async function handleExportLearningDataset() {
        setExportingDataset(true)
        setError('')
        setSuccess('')

        try {
            const response = await axios.get('/api/quality_review/learning_dataset', {
                params: {
                    download: 1,
                    clientId: selectedClient?._id || undefined,
                    limit: 1000
                },
                responseType: 'blob'
            })

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }))
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = selectedClient?._id
                ? `quality-review-learning-dataset-${selectedClient._id}.json`
                : 'quality-review-learning-dataset.json'
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            window.URL.revokeObjectURL(url)
            setSuccess(selectedClient?._id
                ? 'Learning dataset exported for the selected client.'
                : 'Learning dataset exported.'
            )
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to export learning dataset.')
        } finally {
            setExportingDataset(false)
        }
    }

    function applyLoadedReview(review, options = {}) {
        const preservedReviewState = reviewStateRef.current
        const preserveReviewerState = Boolean(options.preserveReviewerState)
        const loadedReviewReadOnly = isQualityReviewReadOnly(review)
        const recoveredDraft = options.skipDraftRecovery || loadedReviewReadOnly
            ? null
            : readReviewDraft(review?.reviewId, review?.updatedAt || review?.createdAt)
        setResult(preserveReviewerState ? { ...review, reviewState: preservedReviewState } : review)
        if (preserveReviewerState) {
            setReviewState(preservedReviewState)
        } else {
            setReviewState(recoveredDraft?.reviewState || review?.reviewState || buildInitialReviewState(review?.sections))
            setReviewStateDirty(Boolean(recoveredDraft))
            setReviewSaveStatus(recoveredDraft
                ? { state: 'recovered', savedAt: '', message: 'Recovered unsaved reviewer decisions from this browser.' }
                : { state: 'saved', savedAt: review?.updatedAt || review?.createdAt || '', message: review?.updatedAt ? `Saved ${formatSavedTime(review.updatedAt)}` : '' }
            )
        }
        setFeedbackState(review?.feedbackState || { findings: {}, corrections: {} })
        if (!options.preserveView) {
            setResultsViewMode('checklist')
        }
        setExpectations(buildReviewExpectationFields(review))

        if (review?.clientId) {
            const client = clients.find(item => item._id === review.clientId)
            if (client) {
                setSelectedClient(client)
            }
        }

        if (recoveredDraft) {
            showNotice('Recovered unsaved review progress. It will autosave shortly.', 'info')
        }
    }

    async function refreshReviewHistory() {
        setLoadingHistory(true)
        const requestedSearch = historySearchRef.current

        try {
            const params = { limit: 50 }
            if (requestedSearch.trim()) params.search = requestedSearch.trim()

            const response = await axios.get('/api/quality_reviews', { params })
            if (requestedSearch === historySearchRef.current) setReviewHistory(Array.isArray(response.data) ? response.data : [])
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to refresh saved reviews.')
        } finally {
            if (requestedSearch === historySearchRef.current) setLoadingHistory(false)
        }
    }

    function scrollToSummary() {
        window.setTimeout(() => {
            summaryRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }, 150)
    }

    const feedbackCounts = buildFeedbackCounts(feedbackState)
    return (
        <Box
            px={{ xs: 2, md: 4 }}
            pt={3}
            pb={{ xs: 6, md: 8 }}
            sx={{
                '& .MuiTypography-body2': { fontSize: '0.98rem', lineHeight: 1.55 },
                '& .MuiTypography-caption': { fontSize: '0.88rem', lineHeight: 1.45 },
                '& .MuiTypography-overline': { fontSize: '0.88rem', lineHeight: 1.5 },
                '& .MuiChip-label': { fontSize: '0.9rem' },
                '& .MuiButton-root': { fontSize: '0.9rem' },
                '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: '0.96rem' },
                '& .MuiInputLabel-shrink': {
                    fontSize: '1.08rem',
                    transform: 'translate(14px, -9px) scale(0.85)'
                }
            }}
        >
            <Stack spacing={3}>
                <Paper
                    sx={{
                        display: { xs: result ? 'none' : 'block', sm: 'block' },
                        p: { xs: 2.5, md: 3.5 },
                        borderRadius: 3,
                        border: '1px solid #d9e4ec',
                        background: 'linear-gradient(135deg, #f4fbff 0%, #ffffff 55%, #f7f4ee 100%)'
                    }}
                >
                    <Stack spacing={2.5}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            alignItems={{ md: 'center' }}
                            justifyContent="space-between"
                            sx={{ width: '100%' }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="overline" sx={{ color: '#47667a', letterSpacing: 1.2, fontWeight: 700 }}>
                                    FMD Reports Check
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#173042', lineHeight: 1.1 }}>
                                    Financial report quality check
                                </Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<FactCheckIcon />}
                                    onClick={() => setSavedReviewsDialogOpen(true)}
                                    sx={smallSettingsButtonSx}
                                >
                                    See past reviews
                                </Button>
                                <Button
                                    variant="text"
                                    startIcon={<InfoOutlinedIcon />}
                                    onClick={() => setHowChecksDialogOpen(true)}
                                    sx={smallSettingsButtonSx}
                                >
                                    How checks work
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>

                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}
                {!result && rerunNotice && <Alert severity="info">{rerunNotice}</Alert>}
                {!result && (
                    <>
                        <StepTracker activeStep={setupStepVisibility.activeStep} />
                        <Stack spacing={2}>
                            <SetupStepCard
                                number={1}
                                title="Upload BizFile"
                                description="Upload the official ACRA BizFile. Add the latest Annual Return when it supplies the current director particulars."
                                sectionRef={bizfileStepRef}
                                status={bizfileExtractionReady ? 'complete' : 'active'}
                            >
                                <Box sx={bizfileUploadStepGridSx}>
                                    <Box sx={uploadBundleCellSx}>
                                        <UploadCard
                                            title="Official ACRA BizFile"
                                            buttonLabel={bizfile ? 'Replace BizFile' : 'Choose BizFile'}
                                            accept=".pdf,.doc,.docx"
                                            multiple={false}
                                            selectedItems={bizfile ? [{
                                                key: fileKey(bizfile),
                                                label: `${bizfile.name} (${formatBytes(bizfile.size)})`,
                                                onDelete: handleClearBizfile
                                            }] : []}
                                            onSelect={handleBizfileSelect}
                                            onDropFiles={handleBizfileSelect}
                                        />
                                    </Box>
                                    <Box sx={{ ...uploadBundleCellSx, flexDirection: 'column' }}>
                                        <UploadCard
                                            title="Latest Annual Return (optional)"
                                            buttonLabel={annualReturn ? 'Replace Annual Return' : 'Choose Annual Return'}
                                            accept=".pdf,.doc,.docx"
                                            multiple={false}
                                            selectedItems={annualReturn ? [{
                                                key: fileKey(annualReturn),
                                                label: `${annualReturn.name} (${formatBytes(annualReturn.size)})`,
                                                onDelete: () => handleAnnualReturnSelect([])
                                            }] : []}
                                            onSelect={handleAnnualReturnSelect}
                                            onDropFiles={handleAnnualReturnSelect}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            The latest AR supplies current directors and their particulars. The ACRA BizFile supplies each matched director’s full name, including recorded pinyin. Unclear matches need confirmation.
                                        </Typography>
                                    </Box>
                                    <Paper variant="outlined" sx={privacyAssuranceSx}>
                                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                            <Box sx={privacyAssuranceIconSx}>
                                                <GppGoodOutlinedIcon sx={{ fontSize: 22 }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 850, color: '#17453d' }}>
                                                    Designed to support Singapore’s PDPA
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#46675f', mt: 0.5, lineHeight: 1.55 }}>
                                                    {sourceAccessPolicyStatus === 'enabled'
                                                        ? 'All uploaded files are encrypted and kept for 14 days for the uploader and one selected colleague, then deleted automatically. Review results remain for up to 30 days.'
                                                        : sourceAccessPolicyStatus === 'disabled'
                                                            ? 'Original files are processed for this review and are not kept in a server archive. Saved results keep issue locations and reviewer decisions, not document copies.'
                                                            : 'Bubbly is confirming the source-file handling policy. The quality check remains unavailable until this succeeds.'}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    startIcon={<GppGoodOutlinedIcon />}
                                                    onClick={() => setPrivacyDialogOpen(true)}
                                                    sx={privacyDetailsButtonSx}
                                                >
                                                    Privacy &amp; PDPA details
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Box>
                                <SourceAccessPolicyNotice
                                    status={sourceAccessPolicyStatus}
                                    onRetry={() => setSourceAccessPolicyRetry(value => value + 1)}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<FactCheckIcon />}
                                        onClick={handleExtractBizfile}
                                        disabled={!bizfile || extractingBizfile}
                                        sx={extractBizfileButtonSx}
                                    >
                                        {extractingBizfile ? 'Extracting…' : bizfileExtractionReady ? 'Re-extract BizFile' : 'Extract BizFile information'}
                                    </Button>
                                </Box>
                            </SetupStepCard>

                            {setupStepVisibility.showStepTwo && <SetupStepCard
                                number={2}
                                title="Confirm source information"
                                description="Check the details extracted from your business-profile documents."
                                status={sourceInformationConfirmed ? 'complete' : 'active'}
                                sectionRef={sourceConfirmationRef}
                                action={(
                                    <Button
                                        variant="outlined"
                                        startIcon={<SettingsIcon />}
                                        onClick={() => setShowOptionalSetup(true)}
                                        sx={smallSettingsButtonSx}
                                    >
                                        Optional setup
                                    </Button>
                                )}
                            >

                        <BizfileSourceConfirmationSection
                            bizfile={bizfile}
                            bizfileExtraction={bizfileExtraction}
                            bizfileExtractionReady={bizfileExtractionReady}
                            expectations={expectations}
                            handleExpectationChange={handleExpectationChange}
                            sourceInformationConfirmed={sourceInformationConfirmed}
                            sourceEditedFields={sourceEditedFields}
                            onConfirmSource={handleConfirmSourceInformation}
                        />
                            </SetupStepCard>}

                            {setupStepVisibility.showStepThree && <SetupStepCard
                                number={3}
                                title="Set period, add files & run"
                                description="Set the financial period, add the working documents, then start the quality check."
                                status="active"
                                sectionRef={stepThreeRef}
                            >
                                <Stack spacing={2.5}>

                        <Box sx={financialPeriodSetupSx}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.5}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                sx={{ mb: 1.75 }}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                        Financial period to check
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Used as the source of truth for FS period, report-year, and tax YA checks.
                                    </Typography>
                                </Box>
                                <Chip label="Required" size="small" sx={requiredChipSx} />
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                                {financialPeriodPresets.map(preset => (
                                    <Chip
                                        key={preset.key}
                                        label={preset.label}
                                        clickable
                                        color={expectations.expectedFinancialPeriodStart === preset.start && expectations.expectedFinancialPeriodEnd === preset.end ? 'primary' : 'default'}
                                        variant={expectations.expectedFinancialPeriodStart === preset.start && expectations.expectedFinancialPeriodEnd === preset.end ? 'filled' : 'outlined'}
                                        onClick={() => handleFinancialPeriodPreset(preset)}
                                        sx={{ fontWeight: 800 }}
                                    />
                                ))}
                            </Stack>

                            <Box sx={financialPeriodGridSx}>
                                <TextField
                                    fullWidth
                                    required
                                    type="date"
                                    label="Financial period start"
                                    value={expectations.expectedFinancialPeriodStart}
                                    onChange={(event) => handleExpectationChange('expectedFinancialPeriodStart', event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    error={financialPeriodInvalid}
                                    helperText=" "
                                    sx={overrideFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    required
                                    type="date"
                                    label="Financial period end"
                                    value={expectations.expectedFinancialPeriodEnd}
                                    onChange={(event) => handleExpectationChange('expectedFinancialPeriodEnd', event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    error={financialPeriodInvalid}
                                    helperText={financialPeriodInvalid ? 'End date must be after start date.' : ' '}
                                    sx={overrideFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    label="Year of Assessment"
                                    placeholder="e.g. 2026"
                                    value={expectations.expectedYa}
                                    onChange={(event) => handleExpectationChange('expectedYa', event.target.value.replace(/^YA\s*/i, ''))}
                                    helperText="Auto-derived from the period end. Edit only when required."
                                    sx={overrideFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    label="Form C-S signatory (optional)"
                                    value={expectations.expectedTaxSignatory || ''}
                                    onChange={(event) => handleExpectationChange('expectedTaxSignatory', event.target.value)}
                                    inputProps={{ maxLength: 240 }}
                                    helperText="Enter the agreed signer for this review. Re-enter it when reopening a saved review; this field is not retained."
                                    sx={overrideFieldSx}
                                />
                            </Box>
                            {expectations.expectedYa !== deriveYaFromDateInput(expectations.expectedFinancialPeriodEnd) && (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleExpectationChange('expectedYa', deriveYaFromDateInput(expectations.expectedFinancialPeriodEnd))}
                                    sx={{ mt: 0.75 }}
                                >
                                    Reset YA from period end
                                </Button>
                            )}
                        </Box>

                        <Box>
                            <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                Documents to review
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                Add only the files needed for this check. At least one document is required.
                            </Typography>
                        </Box>

                        <Box sx={uploadBundlesGridSx}>
                            <Box sx={uploadBundleCellSx}>
                                <UploadCard
                                    title="Upload compilation/audited FS files"
                                    buttonLabel="Add FS files"
                                    description="PDF is preferred. Max 25 MB per file."
                                    accept=".pdf,.doc,.docx,.xlsx,.xls"
                                    multiple
                                    selectedItems={compilationDocuments.map(file => ({
                                        key: fileKey(file),
                                        label: `${file.name} (${formatBytes(file.size)})`,
                                        currentBundleKey: 'compilation_fs',
                                        onMove: target => handleMoveBundleFile('compilation_fs', target, fileKey(file)),
                                        onDelete: () => handleRemoveBundleFile('compilation_fs', fileKey(file))
                                    }))}
                                    onSelect={appendCompilationDocuments}
                                    onDropFiles={appendCompilationDocuments}
                                    onClearAll={() => setCompilationDocuments([])}
                                />
                            </Box>
                            <Box sx={uploadBundleCellSx}>
                                <UploadCard
                                    title="Upload tax computation files"
                                    buttonLabel="Add tax files"
                                    description="PDF is preferred. Max 25 MB per file."
                                    accept=".pdf,.doc,.docx,.xlsx,.xls"
                                    multiple
                                    selectedItems={taxDocuments.map(file => ({
                                        key: fileKey(file),
                                        label: `${file.name} (${formatBytes(file.size)})`,
                                        currentBundleKey: 'tax_computation',
                                        onMove: target => handleMoveBundleFile('tax_computation', target, fileKey(file)),
                                        onDelete: () => handleRemoveBundleFile('tax_computation', fileKey(file))
                                    }))}
                                    onSelect={appendTaxDocuments}
                                    onDropFiles={appendTaxDocuments}
                                    onClearAll={() => setTaxDocuments([])}
                                />
                            </Box>
                            <Box sx={uploadBundleCellSx}>
                                <UploadCard
                                    title="Upload other documents"
                                    buttonLabel="Add other files"
                                    description="PDF is preferred. Max 25 MB per file."
                                    accept=".pdf,.doc,.docx,.xlsx,.xls"
                                    multiple
                                    selectedItems={otherDocuments.map(file => ({
                                        key: fileKey(file),
                                        label: `${file.name} (${formatBytes(file.size)})`,
                                        currentBundleKey: 'other_documents',
                                        onMove: target => handleMoveBundleFile('other_documents', target, fileKey(file)),
                                        onDelete: () => handleRemoveBundleFile('other_documents', fileKey(file))
                                    }))}
                                    onSelect={appendOtherDocuments}
                                    onDropFiles={appendOtherDocuments}
                                    onClearAll={() => setOtherDocuments([])}
                                />
                            </Box>
                        </Box>

                        {uploadValidationMessage && (
                            <Alert severity="error" onClose={() => setUploadValidationMessage('')} sx={{ borderRadius: 2.5 }}>
                                {uploadValidationMessage}
                            </Alert>
                        )}

                        <SourceReviewerSetup
                            enabled={sourceRetentionEnabled}
                            reviewers={sourceReviewerOptions}
                            loading={loadingSourceReviewers}
                            value={selectedSourceReviewer}
                            onChange={setSelectedSourceReviewer}
                        />
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.25}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                backgroundColor: '#f8fafb',
                                border: '1px solid #e4eaef'
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 800, color: '#1b2c39', mb: 0.75 }}>
                                    Ready to run
                                </Typography>
                                <Stack spacing={0.5}>
                                    {reviewReadiness.map(item => (
                                        <Stack key={item.id} direction="row" spacing={0.75} alignItems="center">
                                            {item.ready
                                                ? <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                                                : <ErrorOutlineIcon color="disabled" sx={{ fontSize: 18 }} />}
                                            <Typography variant="body2" color={item.ready ? 'text.primary' : 'text.secondary'}>
                                                {item.label}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<FactCheckIcon />}
                                    onClick={handleRunReview}
                                    disabled={submitting || !reviewReady || !sourceAccessPolicyReady}
                                    sx={runReviewButtonSx}
                                >
                                    {submitting ? 'Running review…' : 'Run review'}
                                </Button>
                            </Stack>
                        </Stack>
                                </Stack>
                            </SetupStepCard>}
                        </Stack>
                    </>
                )}

                {result && (
                    <>
                        {result.persistence?.dryRun && (
                            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                                Dry-run review only. This location-first result was not saved.
                            </Alert>
                        )}
                        <Stack
                            ref={summaryRef}
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            justifyContent="space-between"
                            alignItems={{ md: 'center' }}
                        >
                            <Box>
                                {activeReviewCompanyName && <Typography variant="overline" sx={journeyStepLabelSx}>
                                    Quality check results
                                </Typography>}
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#173042' }}>
                                    {activeReviewCompanyName || 'Quality check results'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatDateTime(result.createdAt)}
                                </Typography>
                                <SourceAccessSummary review={result} />
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                <Button variant="outlined" disabled={reviewActionBusy} onClick={() => setUploadedFilesOpen(true)}>View uploaded files</Button>
                                <Button variant="contained" disabled={reviewActionBusy} onClick={requestAddFilesAndRerun}>Add files &amp; rerun</Button>
                                {!reviewReadOnly && activeSourceAccess.canManage && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<SettingsIcon />}
                                        onClick={() => setSourceAccessDialogOpen(true)}
                                    >
                                        Manage source access
                                    </Button>
                                )}
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<InfoOutlinedIcon />}
                                    onClick={() => setPrivacyDialogOpen(true)}
                                >
                                    Privacy &amp; file handling
                                </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RestartAltIcon />}
                                        onClick={() => reviewStateDirty ? setNewBatchDialogOpen(true) : handleStartNewBatch()}
                                        disabled={reviewActionBusy}
                                        sx={heroActionButtonSx}
                                    >
                                        Start next review
                                    </Button>
                            </Stack>
                        </Stack>

                        {result?.lifecycle?.legacy && (
                            <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
                                <Typography sx={{ fontWeight: 850 }}>Legacy review</Typography>
                                <Typography variant="body2">
                                    {result.lifecycle.reason || 'This review was created before confirmed BizFile sources and versioned checks were introduced. Its findings remain viewable, but rerun the batch before recording decisions.'}
                                </Typography>
                            </Alert>
                        )}

                        {!focusMode && !reviewReadOnly && (
                            <Paper
                                ref={staffToolsRef}
                                component="section"
                                aria-labelledby="quality-review-staff-tools-title"
                                variant="outlined"
                                sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, borderColor: '#bfd6e3', backgroundColor: '#f7fbfd' }}
                            >
                                <Stack spacing={1.25}>
                                    <Box sx={{ px: 0.5 }}>
                                        <Typography id="quality-review-staff-tools-title" sx={{ fontWeight: 900, color: '#173042' }}>
                                            Staff tools
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Review inputs, supporting detail and optional quality feedback.
                                        </Typography>
                                    </Box>

                                    <Accordion disableGutters sx={roundedAccordionSx}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={documentAccordionSummarySx}>
                                            <Stack spacing={0.35}>
                                                <Typography fontWeight={800}>Review inputs and confirmed source</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Source information and uploaded-file context
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                            <BizfileExtractionSection result={result} />
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion disableGutters sx={roundedAccordionSx}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={documentAccordionSummarySx}>
                                            <Stack spacing={0.35}>
                                                <Typography fontWeight={800}>Supporting detail</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Full explanations, file views and optional technical tools
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: { xs: 1, md: 2.5 }, pb: 2.5, pt: 0 }}>
                                            <Stack spacing={2.5}>
                                                <WordingReviewSection result={result} />
                                                <ResultsPanel
                                                    mode={resultsViewMode}
                                                    onChange={setResultsViewMode}
                                                    onOpenSettings={() => setSettingsOpen(true)}
                                                >
                                                    {resultsViewMode === 'checklist' ? (
                                                        <ChecklistSection
                                                            sections={result.sections}
                                                            showTechnicalLabels={showTechnicalLabels}
                                                        />
                                                    ) : (
                                                        <FileResultsSection
                                                            files={result.files}
                                                            fileResults={result.fileResults}
                                                            sections={result.sections}
                                                            reviewState={reviewState}
                                                            showTechnicalLabels={showTechnicalLabels}
                                                        />
                                                    )}
                                                </ResultsPanel>
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>

                                    <OptionalFeedbackStepSection
                                        open={feedbackDialogOpen}
                                        onOpen={() => setFeedbackDialogOpen(true)}
                                        onClose={() => setFeedbackDialogOpen(false)}
                                        result={result}
                                        reviewState={reviewState}
                                        feedbackState={feedbackState}
                                        onFeedbackChange={handleFeedbackChange}
                                        onExport={handleExportLearningDataset}
                                        onSave={handleSaveFeedback}
                                        exportingDataset={exportingDataset}
                                        savingFeedback={savingFeedback}
                                        selectedReviewId={selectedReviewId}
                                        feedbackCounts={feedbackCounts}
                                        showTechnicalLabels={showTechnicalLabels}
                                    />
                                </Stack>
                            </Paper>
                        )}

                        <ClientReadyResults
                            result={result}
                            expectations={activeReviewExpectations}
                            reviewState={reviewState}
                            reviewStateDirty={reviewStateDirty}
                            savingReviewState={savingReviewState}
                            onResolutionChange={reviewReadOnly ? undefined : handleResolutionChange}
                            onNoteChange={reviewReadOnly ? undefined : handleReviewNoteChange}
                            onSaveReviewState={reviewReadOnly ? undefined : handleSaveReviewState}
                            onOpenDocument={handleOpenDocument}
                            availableSourceFileNames={result?.sourceAccess?.retentionEnabled ? [] : localSourceFileNames}
                            availableRetainedSourceFileIds={activeSourceAccess.canOpen ? activeSourceAccess.retainedFileIds : []}
                            openingSourceFileId={openingSourceFileId}
                            onConfirmCorrectClient={reviewReadOnly ? undefined : handleConfirmCorrectClient}
                            onBlockerResolutionChange={reviewReadOnly ? undefined : handleResolutionChange}
                            onBlockerNoteChange={reviewReadOnly ? undefined : handleReviewNoteChange}
                            autosaveEnabled={!reviewReadOnly}
                            autosaveStatus={reviewSaveStatus.state}
                            autosaveError={['error', 'conflict'].includes(reviewSaveStatus.state) ? reviewSaveStatus.message : ''}
                            lastSavedAt={reviewSaveStatus.savedAt}
                            onRetryAutosave={reviewReadOnly ? undefined : () => handleSaveReviewState({ silent: false })}
                            showReviewerActions={!reviewReadOnly && showReviewerActions}
                            feedbackState={feedbackState}
                            onFindingFeedback={reviewReadOnly ? undefined : handleInlineFindingFeedback}
                            savingFeedback={savingFeedback}
                            readOnly={reviewReadOnly}
                            skippedFiles={result.skippedFiles}
                        />
                    </>
                )}
            </Stack>

            <ReviewUploadsDialog
                open={uploadedFilesOpen && Boolean(result)}
                onClose={() => setUploadedFilesOpen(false)}
                entries={uploadedFiles}
                openingFileId={openingSourceFileId}
                onOpen={entry => openSourceNavigationEntry({ ...entry, location: '', pageNumber: 1 }, -1)}
                onRerun={requestAddFilesAndRerun}
                busy={reviewActionBusy}
            />
            <Dialog open={rerunConfirmOpen} onClose={() => setRerunConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogContent><Stack spacing={2}>
                    <Typography variant="h6" fontWeight={800}>Add files and rerun?</Typography>
                    <Typography>Unsaved review decisions will be discarded. Your previous saved result stays in Past reviews.</Typography>
                    <Button onClick={() => setRerunConfirmOpen(false)}>Keep current review</Button>
                    <Button variant="contained" disabled={reviewActionBusy} onClick={prepareAddFilesAndRerun}>Discard unsaved changes and continue</Button>
                </Stack></DialogContent>
            </Dialog>
            <Dialog open={preparingRerun} maxWidth="xs" fullWidth aria-labelledby="rerun-progress-title">
                <DialogContent><Stack spacing={2}>
                    <Typography id="rerun-progress-title" fontWeight={800}>Preparing your uploaded files</Typography>
                    <LinearProgress />
                    <Typography role="status">{rerunProgress}</Typography>
                </Stack></DialogContent>
            </Dialog>
            <InlineSourceViewer
                open={sourceViewer.open}
                source={sourceViewer.source}
                onClose={closeSourceViewer}
                onDownload={handleDownloadSource}
                downloading={Boolean(downloadingSourceFileId)}
            />

            <ManageSourceAccessDialog
                open={sourceAccessDialogOpen && !reviewReadOnly}
                review={result}
                reviewers={sourceReviewerOptions}
                loading={loadingSourceReviewers}
                saving={savingSourceAccess}
                onClose={() => setSourceAccessDialogOpen(false)}
                onSave={handleSaveSourceAccess}
                onRevoke={handleRevokeSourceAccess}
                onDeleteSources={handleDeleteSourcesNow}
            />

            <MissedIssueDialog
                open={missedIssueDialogOpen}
                result={result}
                saving={savingMissedIssue}
                onClose={() => setMissedIssueDialogOpen(false)}
                onSubmit={handleReportMissedIssue}
            />

            <QualityScorecardDialog
                open={scorecardDialogOpen}
                loading={loadingScorecard}
                scorecard={qualityScorecard}
                onClose={() => setScorecardDialogOpen(false)}
            />

            <QualityReviewPrivacyDialog
                open={privacyDialogOpen}
                policyStatus={privacySourceAccessPolicyStatus}
                onClose={() => setPrivacyDialogOpen(false)}
            />

            <Dialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3
                    }
                }}
            >
                <DialogContent sx={{ position: 'relative', pt: 5 }}>
                    <DialogCloseButton onClose={() => setSettingsOpen(false)} />
                    <Stack spacing={2}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                            Results settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            These display settings only affect the current browser session and reset after refresh.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showTechnicalLabels}
                                    onChange={(event) => setShowTechnicalLabels(event.target.checked)}
                                />
                            }
                            label="Show technical labels in all results"
                        />
                    </Stack>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showOptionalSetup}
                onClose={() => setShowOptionalSetup(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <DialogCloseButton onClose={() => setShowOptionalSetup(false)} />
                    <OptionalSetupDialogContent
                        clients={clients}
                        loadingClients={loadingClients}
                        selectedClient={selectedClient}
                        handleClientChange={handleClientChange}
                        expectations={expectations}
                        handleExpectationChange={handleExpectationChange}
                        handleClearManualOverrides={handleClearManualOverrides}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={howChecksDialogOpen}
                onClose={() => setHowChecksDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <DialogCloseButton onClose={() => setHowChecksDialogOpen(false)} />
                    <HowChecksDialogContent />
                </DialogContent>
            </Dialog>

            <Dialog
                open={savedReviewsDialogOpen}
                onClose={() => setSavedReviewsDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <DialogCloseButton onClose={() => setSavedReviewsDialogOpen(false)} />
                    <SavedReviewsDialogContent
                        reviews={reviewHistory}
                        clients={clients}
                        loading={loadingHistory}
                        selectedReviewId={selectedReviewId}
                        onLoadReview={handleLoadSavedReview}
                        searchValue={historySearchTerm}
                        onSearchChange={setHistorySearchTerm}
                        onOpenPrivacy={() => setPrivacyDialogOpen(true)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={newBatchDialogOpen}
                onClose={() => setNewBatchDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogContent sx={{ position: 'relative', pt: 5 }}>
                    <DialogCloseButton onClose={() => setNewBatchDialogOpen(false)} />
                    <Stack spacing={2}>
                        <Typography variant="h6" sx={{ fontWeight: 850, color: '#1b2c39' }}>
                            Start the next review?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Return to Upload BizFile with a blank financial period. The saved review will remain in Past reviews. Any unsaved changes will be discarded.
                        </Typography>
                        {reviewStateDirty && (
                            <Alert severity="warning">Review progress has not been saved.</Alert>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                            <Button variant="outlined" onClick={() => setNewBatchDialogOpen(false)}>
                                Keep current batch
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={savingReviewState || savingFeedback || savingSourceAccess || savingMissedIssue}
                                onClick={() => {
                                    setNewBatchDialogOpen(false)
                                    handleStartNewBatch()
                                }}
                            >
                                Discard changes and start next
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>

            <Dialog
                open={runProgress.active && !progressMinimized}
                onClose={() => setProgressMinimized(true)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    }
                }}
            >
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                            Running review
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#516675' }}>
                            {runProgress.label || 'Processing review...'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#7a8b98' }}>
                            This may take a few minutes.
                        </Typography>
                        {!!runProgress.hint && (
                            <Box
                                sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    backgroundColor: '#f6fafc',
                                    border: '1px solid #e1ebf1'
                                }}
                            >
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: '#607789', mb: 0.25 }}>
                                    Current task
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#355268' }}>
                                    {runProgress.hint}
                                </Typography>
                            </Box>
                        )}
                        <LinearProgress
                            variant={runProgress.mode}
                            value={runProgress.mode === 'determinate' ? runProgress.value : undefined}
                            sx={{
                                height: 10,
                                borderRadius: 999,
                                backgroundColor: '#e8f1f5'
                            }}
                        />
                        {runProgress.mode === 'determinate' && (
                            <Typography variant="body2" sx={{ color: '#355268', fontWeight: 700 }}>
                                {runProgress.value}%
                            </Typography>
                        )}
                        <Button variant="text" onClick={() => setProgressMinimized(true)}>
                            Continue in background
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            {runProgress.active && progressMinimized && (
                <Paper
                    role="status"
                    aria-live="polite"
                    sx={{
                        position: 'fixed',
                        right: { xs: 16, md: 28 },
                        bottom: { xs: 16, md: 28 },
                        width: { xs: 'calc(100% - 32px)', sm: 360 },
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid #cfe0e9',
                        boxShadow: '0 18px 50px rgba(16, 24, 40, 0.18)',
                        zIndex: 1400
                    }}
                >
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 850 }}>{runProgress.label || 'Running review'}</Typography>
                            <Button size="small" onClick={() => setProgressMinimized(false)}>View</Button>
                        </Stack>
                        <LinearProgress
                            variant={runProgress.mode}
                            value={runProgress.mode === 'determinate' ? runProgress.value : undefined}
                        />
                    </Stack>
                </Paper>
            )}

            <Snackbar
                open={toast.open}
                autoHideDuration={4500}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={toast.severity}
                    variant="filled"
                    onClose={() => setToast(prev => ({ ...prev, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

function DialogCloseButton({ onClose }) {
    return (
        <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 5,
                color: '#496273',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                border: '1px solid #e0e8ee',
                '&:hover': {
                    backgroundColor: '#f3f8fb'
                }
            }}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
    )
}

function JourneyStepHeader({ step, title, description, tooltipText = '', action = null }) {
    return (
        <Box
            sx={{
                px: { xs: 2, md: 2.25 },
                py: { xs: 1.35, md: 1.5 },
                borderRadius: 3,
                border: '1px solid #dce7ed',
                background: 'linear-gradient(135deg, #f7fbff 0%, #ffffff 100%)',
                boxShadow: '0 12px 28px rgba(16, 24, 40, 0.04)'
            }}
        >
            <Typography variant="overline" sx={journeyStepLabelSx}>
                {step}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mt: 0.45 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={journeyStepTitleSx}>
                        {title}
                    </Typography>
                    {tooltipText && (
                        <Tooltip
                            title={tooltipText}
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: tooltipReadableSx
                                }
                            }}
                        >
                            <IconButton
                                aria-label={`About ${title}`}
                                size="small"
                                sx={{ p: 0.25, color: '#6d8493' }}
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                {action}
            </Stack>
            {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {description}
                </Typography>
            )}
        </Box>
    )
}

function SetupStepCard({ number, title, description, status = 'upcoming', upcomingMessage = '', action, sectionRef, children }) {
    const statusPresentation = {
        active: {
            label: 'Current step',
            color: '#0c6485',
            backgroundColor: '#e5f5fb',
            borderColor: '#a8d3e5'
        },
        complete: {
            label: 'Completed',
            color: '#2f7046',
            backgroundColor: '#eaf7ee',
            borderColor: '#b9dcc4'
        },
        upcoming: {
            label: 'Next',
            color: '#657986',
            backgroundColor: '#f3f6f8',
            borderColor: '#dfe7eb'
        }
    }[status] || {}
    const titleId = `quality-review-setup-step-${number}`

    return (
        <Paper
            ref={sectionRef}
            component="section"
            aria-labelledby={titleId}
            sx={{
                borderRadius: 3,
                border: status === 'active' ? '1px solid #9fcfe2' : '1px solid #dce6eb',
                boxShadow: status === 'active'
                    ? '0 16px 38px rgba(20, 91, 118, 0.08)'
                    : '0 10px 28px rgba(16, 24, 40, 0.035)',
                overflow: 'hidden'
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{
                    px: { xs: 2, md: 2.75 },
                    py: 2,
                    backgroundColor: status === 'active' ? '#f4fbfe' : '#fbfcfd',
                    borderBottom: '1px solid #e4ebef'
                }}
            >
                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontWeight: 900,
                            color: statusPresentation.color,
                            backgroundColor: statusPresentation.backgroundColor,
                            border: `1px solid ${statusPresentation.borderColor}`
                        }}
                    >
                        {number}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography id={titleId} variant="h6" sx={{ fontWeight: 850, color: '#173042', lineHeight: 1.3 }}>
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                            {description}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Chip
                        label={statusPresentation.label}
                        size="small"
                        sx={{
                            fontWeight: 800,
                            color: statusPresentation.color,
                            backgroundColor: statusPresentation.backgroundColor,
                            border: `1px solid ${statusPresentation.borderColor}`
                        }}
                    />
                    {action}
                </Stack>
            </Stack>
            <Box sx={{ p: { xs: 2, md: 2.75 } }}>
                {status === 'upcoming' ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {upcomingMessage || 'Complete the previous step to continue.'}
                    </Alert>
                ) : children}
            </Box>
        </Paper>
    )
}

function StepTracker({ activeStep = 1 }) {
    const steps = [
        { number: 1, shortTitle: 'Upload BizFile', tooltip: 'Add the source BizFile for this review.' },
        { number: 2, shortTitle: 'Confirm source', tooltip: 'Extract, check and explicitly confirm the source-of-truth values.' },
        { number: 3, shortTitle: 'Add files & run', tooltip: 'Set the financial period, add review documents, and run the check.' }
    ]

    return (
        <Paper
            sx={{
                display: { xs: 'none', md: 'block' },
                p: { xs: 1.75, md: 2.25 },
                borderRadius: 3,
                border: '1px solid #dce7ed',
                boxShadow: '0 14px 36px rgba(16, 24, 40, 0.04)'
            }}
        >
            <Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: 1.25
                    }}
                >
                    {steps.map((step, index) => {
                        const isActive = step.number === activeStep
                        const isComplete = step.number < activeStep

                        return (
                            <Box
                                key={step.number}
                                sx={{
                                    p: 1.25,
                                    borderRadius: 2.5,
                                    border: isActive ? '1px solid #99c7df' : '1px solid #e5edf2',
                                    backgroundColor: isActive ? '#eef8fd' : isComplete ? '#f4fbf6' : '#fbfdff',
                                    minWidth: 0
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 800,
                                            color: isActive ? '#0c6485' : isComplete ? '#2f7a49' : '#607789',
                                            backgroundColor: isActive ? '#d8eef8' : isComplete ? '#dff2e5' : '#edf3f7',
                                            flexShrink: 0
                                        }}
                                    >
                                        {step.number}
                                    </Box>
                                    <Box minWidth={0}>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#607789', fontWeight: 700 }}>
                                            Step {step.number}
                                        </Typography>
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Typography sx={{ fontWeight: 800, color: '#1b2c39', lineHeight: 1.2 }}>
                                                {step.shortTitle}
                                            </Typography>
                                            <Tooltip title={step.tooltip} arrow>
                                                <IconButton
                                                    aria-label={`About ${step.shortTitle}`}
                                                    size="small"
                                                    sx={{ p: 0.25, color: '#7b8e9b' }}
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </Stack>
                                {index < steps.length - 1 && (
                                    <Box
                                        sx={{
                                            display: 'block',
                                            mt: 1.1,
                                            height: 2,
                                            borderRadius: 999,
                                            backgroundColor: step.number < activeStep ? '#9dd1ae' : '#dde8ef'
                                        }}
                                    />
                                )}
                            </Box>
                        )
                    })}
                </Box>
            </Stack>
        </Paper>
    )
}

function HowChecksDialogContent() {
    return (
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                        How the system checks your files
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6d5a22', mt: 1, lineHeight: 1.7 }}>
                        This guide explains exactly how the system reads the uploaded BizFile, which checklist items apply to which files, what gets checked automatically, and what still needs reviewer judgment.
                    </Typography>
                </Box>
                <Stack spacing={2}>
                    <HowChecksInfoBlock
                        title="Source of truth"
                        items={[
                            'The official ACRA BizFile supplies company details and full director names, including recorded pinyin. When a latest Annual Return is uploaded, it supplies the current director roster and particulars; only clearly matched directors receive the ACRA full name.',
                            'BizFile company-name extraction is cleaned against nearby labels such as entity type, entity status, registered office address, and position holders so those labels are not accidentally treated as part of the company name.',
                            'If the BizFile source values look suspicious, deterministic auto-corrections are blocked until the extracted source-of-truth information is manually confirmed.',
                            'The staff-confirmed financial period entered during upload is used as the authority for period-sensitive FS and tax checks.',
                            'If the BizFile extraction is incomplete or wrong, Optional setup can override the company source values manually for the current review only.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="Which checklist applies to which bundle"
                        items={[
                            'Compilation / audited FS files run the common checklist items plus the FS-specific checklist.',
                            'Tax computation files run the common checklist items plus the tax-specific checklist.',
                            'Other documents run only the common checklist items.',
                            'The system does not require every bundle to be uploaded. It only runs the relevant sections for the bundles that are present.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="Common checklist rules"
                        items={[
                            'The system does not force every common field onto every file. It only checks the fields that are expected for that document template.',
                            'UEN is checked only on templates that are expected to carry a UEN, such as FS Cover and FS Part II workbook.',
                            'Registered address is checked only on templates that are expected to show the registered office or company address, such as FS Part I, engagement letters, and engagement / resolution files.',
                            'Director names are checked only on templates that are expected to mention directors.',
                            'Director-name detection is context-based. The system looks for names where the word “director” or “directors” is stated nearby, instead of relying on loose person-name matches alone.',
                            'FMD outgoing cover letters are not forced to carry the client company fields because they may naturally contain FMD’s own details.',
                            'Financial period end is checked only on templates that are expected to carry period-end wording. When staff enter a full period end date, the year is checked against the document wording.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="Company name rule"
                        items={[
                            'Company name punctuation must match the BizFile exactly. Punctuation differences are not accepted automatically.',
                            'Capitalisation may follow the document style, including ALL CAPS, title case, or a mixed-case legal suffix such as “Pte. Ltd.”.',
                            'Different words, missing words, or legal-suffix punctuation differences are still flagged.',
                            'For FS Part I specifically, the system checks the page header across the document and the early report / General Information section only.',
                            'For FS Part I, the system does not require the full company name to keep appearing from page 5 point 2 onwards through the Notes, because those sections may legitimately use “The Company”.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="FS-specific checklist rules"
                        items={[
                            'Contents page checks compare extracted contents entries against detected headings and inferred / rendered page references where possible.',
                            'Two-director checks compare the BizFile directors against director wording, singular vs plural structure, signatory wording, and sign-off labels such as “DIRECTOR” vs “DIRECTORS”.',
                            'Report-year checks derive the expected report year from the source-of-truth FYE or YA and compare it against the year references found in the FS files.',
                            'Notes-to-accounts sequence checks look for numbered note headings and flag jumps, duplicates, or suspicious ordering issues.',
                            'Net-assets vs net-liabilities checks are strongest in the FS Part II workbook, where the system can inspect workbook rows and statement relationships.',
                            'Same-date checks compare governing dates across the report, resolution / LOR, engagement letter, and related documents when the system can classify their role and extract a governing date.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="Tax-specific checklist rules"
                        items={[
                            'YA checks focus on the expected tax tabs and look for conflicting Year of Assessment values.',
                            'Tax print / alignment checks are workbook-based and inspect print areas, hidden columns, hidden tabs, and print-coverage signals.',
                            'Tax schedule numbering checks use worksheet names, schedule markers, and numbering patterns to look for gaps or unusual jumps.',
                            'For standard FMD tax workbooks, the system also checks known operational review points such as sheet 2 column J visibility, K6 bottom border, misplaced sheet 2 schedule labels, sheet 7 medical-fee cell F49, and blank zero-fill cells in sheet 8.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="Visual and wording rules"
                        items={[
                            'Wording review runs automatically on narrative Word and PDF files and checks British / Singapore English spelling, repeated words, punctuation spacing, repeated punctuation, template placeholders, legal-suffix consistency such as “Pte Ltd” vs “Pte. Ltd.”, and headings that end with punctuation.',
                            'Phrase-level wording checks include common FMD review issues such as “judgment” to “judgement”, “United State dollars” to “United States dollars”, and “are the only key personnel of” to “are the key personnel of”.',
                            'For page-sensitive FS checks, upload the PDF version. The system can use PDF pages to make contents-page, note-page, and carried-forward “cont’d” findings more specific.',
                            'Missing full-stop checks are note-aware for FS reports and page-aware where Word/PDF page data is available, so findings should point users to the closest note, page, or document role instead of a raw XML paragraph.',
                            'FS Part I layout checking is currently limited. The system can flag heading-format inconsistency, orphan headings, page-sequence signals, and some rendered-output layout cues, but it does not yet fully validate every paragraph alignment in the Word body.',
                            'Excel blank spacing, hidden template rows, and support-workbook tab naming differences are ignored for missing-line checks because FMD workbooks commonly use them intentionally.',
                            'Quality Review never changes the uploaded files or stores corrected copies. It gives staff the exact location needed to amend their own Word, Excel or PDF working copy.',
                            'Uploaded document content is not sent to external AI services. The checks shown here are the deterministic Quality Review checks.'
                        ]}
                    />
                    <HowChecksInfoBlock
                        title="Wrong-client and pack-level protection"
                        items={[
                            'The system compares each file against the BizFile and can raise a wrong-client alert if the file appears to belong to another company.',
                            'FMD outgoing letters may naturally contain FMD’s own UEN, address, directors, or entity details. Those FMD internal values are ignored for wrong-client checks so they do not create false alerts.',
                            'It also compares documents against each other across the review pack, so it can flag files that are internally inconsistent even if some individual fields still look plausible.',
                            'If a file is strongly suspected to belong to another client, auto-correction is blocked for that file.'
                        ]}
                    />
                </Stack>
            </Stack>
        </Box>
    )
}

function OptionalSetupDialogContent({
    clients,
    loadingClients,
    selectedClient,
    handleClientChange,
    expectations,
    handleExpectationChange,
    handleClearManualOverrides
}) {
    return (
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                        Optional additional setup
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Use this only when you want to link the review to an existing client or override the BizFile-derived source-of-truth values for the current review.
                    </Typography>
                </Box>

                <Box sx={clientFieldRowSx}>
                    <Autocomplete
                        fullWidth
                        sx={selectorAutocompleteSx}
                        options={clients}
                        loading={loadingClients}
                        value={selectedClient}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        getOptionLabel={(option) => option?.clientInfo?.companyName || ''}
                        onChange={handleClientChange}
                        noOptionsText="No authorised client links"
                        renderOption={(props, option) => (
                            <Box component="li" {...props} sx={selectorOptionSx}>
                                <Typography sx={{ fontWeight: 700, color: '#142534', whiteSpace: 'normal' }}>
                                    {option?.clientInfo?.companyName || 'Unnamed client'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#60707f', whiteSpace: 'normal' }}>
                                    {option?.clientInfo?.roc || 'No UEN'}
                                </Typography>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                label="Client"
                                placeholder="Optional client link"
                                helperText="Optional. Only client links authorised for your account are shown."
                                sx={selectorInputSx}
                            />
                        )}
                    />
                </Box>

                <Box sx={advancedOverridesSx}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ sm: 'flex-start' }}
                        sx={{ mb: 2 }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 700, color: '#1a3140' }}>
                                Manual override
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Use these only if the BizFile OCR is incomplete or you need to force a specific source-of-truth value.
                            </Typography>
                        </Box>

                        <Button
                            variant="text"
                            size="small"
                            onClick={handleClearManualOverrides}
                            sx={{
                                alignSelf: { xs: 'flex-start', sm: 'center' },
                                textTransform: 'none',
                                fontWeight: 700
                            }}
                        >
                            Clear all
                        </Button>
                    </Stack>

                    <Box sx={manualOverrideGridSx}>
                        <Box sx={manualOverrideCellSx}>
                            <TextField
                                fullWidth
                                label="Expected company name"
                                value={expectations.expectedCompanyName}
                                onChange={(event) => handleExpectationChange('expectedCompanyName', event.target.value)}
                                sx={overrideFieldSx}
                            />
                        </Box>
                        <Box sx={manualOverrideCellSx}>
                            <TextField
                                fullWidth
                                label="Expected UEN"
                                value={expectations.expectedUen}
                                onChange={(event) => handleExpectationChange('expectedUen', event.target.value.toUpperCase())}
                                sx={overrideFieldSx}
                            />
                        </Box>
                        <Box sx={manualOverrideCellSx}>
                            <TextField
                                fullWidth
                                label="Expected registered address"
                                value={expectations.expectedRegisteredAddress}
                                onChange={(event) => handleExpectationChange('expectedRegisteredAddress', event.target.value)}
                                sx={overrideFieldSx}
                            />
                        </Box>
                        <Box sx={manualOverrideCellSx}>
                            <TextField
                                fullWidth
                                label="Expected directors"
                                placeholder="Separate people with semicolons"
                                value={expectations.expectedDirectors}
                                onChange={(event) => handleExpectationChange('expectedDirectors', event.target.value)}
                                sx={overrideFieldSx}
                            />
                        </Box>
                        <Box sx={manualOverrideCellSx}>
                            <TextField
                                fullWidth
                                label="Expected company secretaries"
                                placeholder="Separate people with semicolons"
                                value={expectations.expectedCompanySecretaries}
                                onChange={(event) => handleExpectationChange('expectedCompanySecretaries', event.target.value)}
                                sx={overrideFieldSx}
                            />
                        </Box>
                        <Box sx={manualOverrideCellSx}>
                            <TextField
                                fullWidth
                                label="Expected country of domicile"
                                value={expectations.expectedCountryOfDomicile}
                                onChange={(event) => handleExpectationChange('expectedCountryOfDomicile', event.target.value)}
                                sx={overrideFieldSx}
                            />
                        </Box>
                    </Box>
                </Box>
            </Stack>
        </Box>
    )
}

function BizfileSourceConfirmationSection({
    bizfile,
    bizfileExtraction,
    bizfileExtractionReady,
    expectations,
    handleExpectationChange,
    sourceInformationConfirmed,
    sourceEditedFields,
    onConfirmSource
}) {
    const sourceQuality = bizfileExtraction?.sourceOfTruth?.sourceQuality || {}
    const warnings = filterBizfileSourceWarnings(sourceQuality.warnings)
    const showSourceFields = Boolean(bizfileExtractionReady)

    return (
        <Box sx={sourceConfirmationSx}>
            <Stack spacing={2}>
                <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                    Company details from BizFile
                </Typography>

                {!bizfile && (
                    <Alert severity="info" sx={sourceAlertSx}>
                        Upload the BizFile first. The extracted information will appear here before the staff upload the FS, tax, and other documents.
                    </Alert>
                )}

                {bizfile && !showSourceFields && (
                    <Alert severity="info" sx={sourceAlertSx}>
                        BizFile selected. Extract the source information and confirm it before running the review.
                    </Alert>
                )}

                {showSourceFields && warnings.length > 0 && (
                    <Alert severity={sourceQuality.autoFixSafe === false ? 'warning' : 'info'} sx={sourceAlertSx}>
                        {warnings.slice(0, 3).join(' ')}
                    </Alert>
                )}

                {showSourceFields && (
                    <Alert
                        severity={sourceInformationConfirmed ? 'success' : sourceEditedFields.length ? 'warning' : 'info'}
                        sx={sourceAlertSx}
                    >
                        {sourceInformationConfirmed
                            ? 'Source information confirmed. The review will use these staff-confirmed values.'
                            : sourceEditedFields.length
                                ? 'One or more extracted values were edited. Confirm the updated source information before running the review.'
                                : 'Check extracted information below, then click on confirm to proceed.'}
                    </Alert>
                )}

                {showSourceFields && (
                    <Box sx={sourceConfirmationGridSx}>
                        <TextField
                            fullWidth
                            label="Company name"
                            value={expectations.expectedCompanyName}
                            onChange={(event) => handleExpectationChange('expectedCompanyName', event.target.value)}
                            sx={overrideFieldSx}
                        />
                        <TextField
                            fullWidth
                            label="UEN"
                            value={expectations.expectedUen}
                            onChange={(event) => handleExpectationChange('expectedUen', event.target.value.toUpperCase())}
                            sx={overrideFieldSx}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Registered address"
                            value={expectations.expectedRegisteredAddress}
                            onChange={(event) => handleExpectationChange('expectedRegisteredAddress', event.target.value)}
                            sx={overrideFieldSx}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Directors"
                            placeholder="Separate people with semicolons"
                            value={expectations.expectedDirectors}
                            onChange={(event) => handleExpectationChange('expectedDirectors', event.target.value)}
                            sx={overrideFieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Company secretaries"
                            placeholder="Separate people with semicolons"
                            value={expectations.expectedCompanySecretaries}
                            onChange={(event) => handleExpectationChange('expectedCompanySecretaries', event.target.value)}
                            sx={overrideFieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Country of domicile"
                            value={expectations.expectedCountryOfDomicile}
                            onChange={(event) => handleExpectationChange('expectedCountryOfDomicile', event.target.value)}
                            sx={overrideFieldSx}
                        />
                    </Box>
                )}

                {showSourceFields && sourceEditedFields.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {sourceEditedFields.map(field => (
                            <Chip
                                key={field}
                                size="small"
                                variant="outlined"
                                color="warning"
                                label={`Edited: ${sourceFieldLabel(field)}`}
                            />
                        ))}
                    </Stack>
                )}

                {showSourceFields && (
                    <Button
                        variant={sourceInformationConfirmed ? 'outlined' : 'contained'}
                        color={sourceInformationConfirmed ? 'success' : 'primary'}
                        startIcon={<CheckCircleIcon />}
                        onClick={onConfirmSource}
                        disabled={sourceInformationConfirmed}
                        sx={{ width: 'fit-content' }}
                    >
                        {sourceInformationConfirmed ? 'Source information confirmed' : 'Confirm'}
                    </Button>
                )}
            </Stack>
        </Box>
    )
}

function sourceFieldLabel(field) {
    const labels = {
        expectedCompanyName: 'Company name',
        expectedUen: 'UEN',
        expectedRegisteredAddress: 'Registered address',
        expectedDirectors: 'Directors',
        expectedCompanySecretaries: 'Company secretaries',
        expectedCountryOfDomicile: 'Country of domicile'
    }
    return labels[field] || field
}

function HowChecksInfoBlock({ title, items }) {
    return (
        <Accordion
            disableGutters
            sx={{
                borderRadius: '12px !important',
                backgroundColor: '#ffffff',
                border: '1px solid #ece6d8',
                boxShadow: 'none',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={1}>
                    {(items || []).map((item, index) => (
                        <Typography key={index} variant="body2" sx={{ color: '#4e5f6b', lineHeight: 1.6 }}>
                            • {item}
                        </Typography>
                    ))}
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

function OptionalFeedbackStepSection({
    open,
    onOpen,
    onClose,
    result,
    reviewState,
    feedbackState,
    onFeedbackChange,
    onExport,
    onSave,
    exportingDataset,
    savingFeedback,
    selectedReviewId,
    feedbackCounts,
    showTechnicalLabels
}) {
    return (
        <Paper
            sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                border: '1px solid #e4e8eb',
                boxShadow: '0 14px 36px rgba(16, 24, 40, 0.04)'
            }}
        >
            <Stack spacing={2}>
                <JourneyStepHeader
                    step="Step 5 (Optional)"
                    title="Provide feedback to the system"
                    action={(
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<FactCheckIcon />}
                            onClick={onOpen}
                            sx={heroActionButtonSx}
                        >
                            Open feedback dialog
                        </Button>
                    )}
                />
            </Stack>

            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minHeight: { xs: '88vh', md: '86vh' },
                        maxHeight: '92vh'
                    }
                }}
            >
                <ReviewFeedbackDialogContent
                    result={result}
                    reviewState={reviewState}
                    feedbackState={feedbackState}
                    onFeedbackChange={onFeedbackChange}
                    onExport={onExport}
                    onSave={onSave}
                    onClose={onClose}
                    exportingDataset={exportingDataset}
                    savingFeedback={savingFeedback}
                    selectedReviewId={selectedReviewId}
                    feedbackCounts={feedbackCounts}
                    showTechnicalLabels={showTechnicalLabels}
                />
            </Dialog>
        </Paper>
    )
}

function ReviewFeedbackDialogContent({
    result,
    reviewState,
    feedbackState,
    onFeedbackChange,
    onExport,
    onSave,
    onClose,
    exportingDataset,
    savingFeedback,
    selectedReviewId,
    feedbackCounts,
    showTechnicalLabels
}) {
    const findingItems = buildFeedbackFindingItems(result, reviewState)

    return (
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: { xs: '88vh', md: '86vh' }, position: 'relative' }}>
            <DialogCloseButton onClose={onClose} />
            <Stack spacing={2.5} sx={{ p: { xs: 2.5, md: 3 }, flex: 1, minHeight: 0 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
                    <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h5" sx={{ fontWeight: 850, color: '#1b2c39' }}>
                                Reviewer feedback
                            </Typography>
                            <Tooltip
                                title="Optional feedback for system learning. These labels do not change the current review results. They help us analyse which findings were correct, wrong, or not applicable, so future batches can have better rule tuning, confidence scoring, and fewer false positives."
                                arrow
                                componentsProps={{
                                    tooltip: {
                                        sx: tooltipReadableSx
                                    }
                                }}
                            >
                                <IconButton aria-label="About reviewer feedback" size="small" sx={{ p: 0.25, color: '#6d8493' }}>
                                    <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.25
                    }}
                >
                    <FeedbackStatCard label="Findings labelled" value={feedbackCounts.findings} />
                    <FeedbackStatCard label="Available feedback items" value={findingItems.length} />
                </Box>

                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, borderColor: '#e0e9ef' }}>
                    <FeedbackItemsSection
                        title="Findings"
                        emptyLabel="No findings are available for feedback."
                        items={findingItems}
                        feedbackState={feedbackState?.findings || {}}
                        options={FINDING_FEEDBACK_OPTIONS}
                        onDecisionChange={(id, value) => onFeedbackChange('findings', id, 'decision', value)}
                        onNoteChange={(id, value) => onFeedbackChange('findings', id, 'note', value)}
                    />
                </Paper>
            </Stack>
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    px: { xs: 2.5, md: 3 },
                    py: 2,
                    borderTop: '1px solid #e4e8eb',
                    backgroundColor: '#ffffff',
                    zIndex: 2
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                    {showTechnicalLabels && (
                        <Button
                            variant="outlined"
                            onClick={onExport}
                            disabled={exportingDataset}
                            sx={heroActionButtonSx}
                        >
                            {exportingDataset ? 'Exporting dataset…' : 'Export learning dataset'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={onSave}
                        disabled={!selectedReviewId || savingFeedback}
                        sx={heroActionButtonSx}
                    >
                        {savingFeedback ? 'Saving feedback…' : 'Save feedback'}
                    </Button>
                </Stack>
            </Box>
        </DialogContent>
    )
}

function FeedbackStatCard({ label, value }) {
    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid #dbe7ef',
                background: 'linear-gradient(135deg, #f8fbfd 0%, #ffffff 100%)'
            }}
        >
            <Typography variant="caption" sx={{ color: '#607789', fontWeight: 800, letterSpacing: 0.4 }}>
                {label}
            </Typography>
            <Typography sx={{ color: '#173042', fontWeight: 850, fontSize: '1.45rem', mt: 0.25 }}>
                {value || 0}
            </Typography>
        </Box>
    )
}

function FeedbackItemsSection({ title, emptyLabel, items, feedbackState, options, onDecisionChange, onNoteChange }) {
    return (
        <Box>
            <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <Typography sx={{ fontWeight: 850, color: '#1b2c39' }}>
                        {title}
                    </Typography>
                    <Chip size="small" variant="outlined" label={`${items.length} item${items.length === 1 ? '' : 's'}`} />
                </Stack>

                {!items.length ? (
                    <Typography variant="body2" color="text.secondary">
                        {emptyLabel}
                    </Typography>
                ) : (
                    <Stack spacing={1.25} sx={{ maxHeight: { xs: '48vh', md: '52vh' }, overflowY: 'auto', pr: 0.5 }}>
                        {items.map(item => (
                            <Box
                                key={item.id}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    border: '1px solid #e4edf3',
                                    backgroundColor: '#ffffff'
                                }}
                            >
                                <Stack spacing={1.25}>
                                    <Box>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }}>
                                            <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                                {item.title}
                                            </Typography>
                                            {item.statusLabel && (
                                                <Chip size="small" variant="outlined" label={item.statusLabel} />
                                            )}
                                        </Stack>
                                        {item.fileName && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                {item.fileName}
                                            </Typography>
                                        )}
                                        {item.summary && (
                                            <Typography variant="body2" sx={{ color: '#334e5f', mt: 0.75, lineHeight: 1.55 }}>
                                                {item.summary}
                                            </Typography>
                                        )}
                                    </Box>
                                    <ReviewerFeedbackEditor
                                        title="Feedback label"
                                        value={feedbackState?.[item.id] || {}}
                                        options={options}
                                        onDecisionChange={(value) => onDecisionChange(item.id, value)}
                                        onNoteChange={(value) => onNoteChange(item.id, value)}
                                    />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Box>
    )
}

function BizfileExtractionSection({ result }) {
    const sourceOfTruth = result?.sourceOfTruth || {}
    const bizfile = sourceOfTruth.bizfile || {}
    const extracted = bizfile?.extractedFacts?.bizfileReference || {}
    const expectations = sourceOfTruth.expectations || {}
    const directors = expectations.expectedDirectors?.length
        ? expectations.expectedDirectors
        : (extracted.directors || [])
    const companySecretaries = expectations.expectedCompanySecretaries?.length
        ? expectations.expectedCompanySecretaries
        : (extracted.companySecretaries || [])
    const locationOnlyResult = result?.retention?.originalFilesPersisted === false
        && Number(result?.retention?.sourceRetentionDays) === 0

    if (locationOnlyResult) {
        return (
            <Paper
                sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 3,
                    border: '1px solid #dce7ed',
                    boxShadow: '0 14px 36px rgba(16, 24, 40, 0.04)'
                }}
            >
                <Stack spacing={1.25}>
                    <Box>
                        <Typography variant="overline" sx={journeyStepLabelSx}>Step 2</Typography>
                        <Typography variant="h6" sx={{ ...journeyStepTitleSx, mt: 0.45 }}>
                            Source information confirmed
                        </Typography>
                    </Box>
                    <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                        Use the confirmed BizFile and the file locations shown in the findings below.
                    </Alert>
                </Stack>
            </Paper>
        )
    }

    return (
        <Paper
            sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                border: '1px solid #dce7ed',
                boxShadow: '0 14px 36px rgba(16, 24, 40, 0.04)'
            }}
        >
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="overline" sx={journeyStepLabelSx}>
                        Step 2
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.45 }}>
                        <Typography variant="h6" sx={journeyStepTitleSx}>
                            Confirmed source information
                        </Typography>
                        <Tooltip
                            title="Confirmed values from older reviews may appear here."
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: tooltipReadableSx
                                }
                            }}
                        >
                            <IconButton aria-label="About confirmed source information" size="small" sx={{ p: 0.25, color: '#6d8493' }}>
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>

                <Box sx={manualOverrideGridSx}>
                    <InfoValueCard label="Company name" value={expectations.expectedCompanyName || extracted.companyName} originalValue={extracted.companyName} />
                    <InfoValueCard label="UEN" value={expectations.expectedUen || extracted.uen} originalValue={extracted.uen} />
                    <InfoValueCard label="Registered address" value={expectations.expectedRegisteredAddress || extracted.registeredAddress} originalValue={extracted.registeredAddress} />
                    <InfoValueCard label="Financial period start" value={expectations.expectedFinancialPeriodStart} />
                    <InfoValueCard label="Financial period end" value={expectations.expectedFinancialPeriodEnd || expectations.expectedFye || extracted.financialYearEnd} />
                    <InfoValueCard label="Year of Assessment" value={expectations.expectedYa} />
                    <InfoValueCard label="Entity status" value={extracted.entityStatus} />
                    <InfoValueCard label="Date of incorporation" value={extracted.incorporationDate} />
                    <InfoValueCard label="Directors" value={directors.join(', ')} originalValue={(extracted.directors || []).join(', ')} />
                    <InfoValueCard label="Company secretaries" value={companySecretaries.join(', ')} originalValue={(extracted.companySecretaries || []).join(', ')} />
                    <InfoValueCard label="Country of domicile" value={expectations.expectedCountryOfDomicile || extracted.countryOfDomicile} originalValue={extracted.countryOfDomicile} />
                </Box>
            </Stack>
        </Paper>
    )
}

function WordingReviewSection({ result }) {
    const wordingReview = result?.wordingReview || {}
    const files = Array.isArray(wordingReview.files) ? wordingReview.files : []
    const hasRun = Boolean(wordingReview.generatedAt)

    if (!result) {
        return null
    }

    return (
        <Accordion
            defaultExpanded={hasRun}
            disableGutters
            sx={{
                border: '1px solid #dce7ed',
                borderRadius: '16px !important',
                overflow: 'hidden',
                boxShadow: '0 14px 36px rgba(16, 24, 40, 0.04)',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1.35, md: 1.5 },
                    '& .MuiAccordionSummary-content': {
                        my: 0.25
                    }
                }}
            >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }} alignItems={{ md: 'center' }}>
                    <Box flex={1}>
                        <Typography variant="overline" sx={journeyStepLabelSx}>
                            Step 3
                        </Typography>
                        <Typography variant="h6" sx={journeyStepTitleSx}>
                            Wording review
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.55 }}>
                            {hasRun
                                ? wordingReview.summary || 'Wording review completed.'
                                : 'Wording review results are not available for this review yet.'
                            }
                        </Typography>
                    </Box>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 }, pt: 0 }}>
                {!hasRun ? (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        Wording review results are not available for this review yet.
                    </Alert>
                ) : files.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No narrative Word or PDF files were available for wording review.
                    </Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {files.map(file => (
                            <Accordion
                                key={file.fileId || file.fileName}
                                disableGutters
                                sx={{
                                    border: '1px solid #e4e8eb',
                                    borderRadius: '14px !important',
                                    overflow: 'hidden',
                                    boxShadow: 'none',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }} alignItems={{ md: 'center' }}>
                                        <Box flex={1}>
                                            <Typography sx={{ fontWeight: 700, color: '#1b2c39' }}>
                                                {file.fileName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {file.summary || 'No wording findings were generated.'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            color={file.status === 'error' ? 'error' : 'default'}
                                            label={file.status === 'error' ? 'Review failed' : `${(file.findings || []).length} finding${(file.findings || []).length === 1 ? '' : 's'}`}
                                        />
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {!(file.findings || []).length ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No wording suggestions were returned for this file.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {(file.findings || []).map(finding => (
                                                <Box key={finding.id} sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#f8fafb', border: '1px solid #e7edf1' }}>
                                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                                                        <Box flex={1}>
                                                            <Typography sx={{ fontWeight: 700 }}>
                                                                {finding.title}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {humanizeFindingCategory(finding.category)}
                                                            </Typography>
                                                        </Box>
                                                        <Chip size="small" color={chipColorForWordingSeverity(finding.severity)} label={humanizeSeverity(finding.severity)} />
                                                    </Stack>
                                                    {finding.issue && (
                                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                                            {finding.issue}
                                                        </Typography>
                                                    )}
                                                    {finding.originalText && (
                                                        <Typography variant="body2" sx={{ mt: 1, color: '#516675' }}>
                                                            Current: {finding.originalText}
                                                        </Typography>
                                                    )}
                                                    {finding.proposedText && (
                                                        <Typography variant="body2" sx={{ mt: 0.75, color: '#1b2c39', fontWeight: 700 }}>
                                                            Suggested: {finding.proposedText}
                                                        </Typography>
                                                    )}
                                                    {finding.suggestion && (
                                                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                                                            {finding.suggestion}
                                                        </Typography>
                                                    )}
                                                    {(finding.locationHint || finding.rationale || finding.snippet) && (
                                                        <Typography variant="body2" sx={{ mt: 0.75, color: '#516675' }}>
                                                            {[finding.locationHint, finding.rationale, finding.snippet].filter(Boolean).join(' ')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    )
}

function ResultsPanel({ mode, onChange, onOpenSettings, children }) {
    return (
        <Paper
            sx={{
                borderRadius: 3,
                border: '1px solid #e4e8eb',
                boxShadow: '0 14px 36px rgba(16, 24, 40, 0.04)',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    px: { xs: 1.5, md: 2 },
                    pt: 1.5,
                    pb: 1.5,
                    borderBottom: '1px solid #e4e8eb',
                    backgroundColor: '#f8fbfd'
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        <Button
                            variant="text"
                            onClick={() => onChange('file')}
                            sx={resultsMenuButtonSx(mode === 'file')}
                        >
                            By file
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => onChange('checklist')}
                            sx={resultsMenuButtonSx(mode === 'checklist')}
                        >
                            By checklist item
                        </Button>
                    </Stack>
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={onOpenSettings}
                        sx={{
                            ...smallSettingsButtonSx,
                            alignSelf: { xs: 'flex-start', sm: 'center' }
                        }}
                    >
                        Settings
                    </Button>
                </Stack>
            </Box>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                {children}
            </Box>
        </Paper>
    )
}

function InfoValueCard({ label, value, originalValue = '' }) {
    const wasEdited = Boolean(
        originalValue &&
        value &&
        String(originalValue).replace(/\s+/g, ' ').trim().toLowerCase()
            !== String(value).replace(/\s+/g, ' ').trim().toLowerCase()
    )

    return (
        <Box
            sx={{
                border: '1px solid #e3eaef',
                borderRadius: 2.5,
                p: 2,
                minWidth: 0,
                backgroundColor: '#fbfdff'
            }}
        >
            <Typography variant="caption" sx={{ color: '#657988', fontWeight: 700, letterSpacing: 0.35 }}>
                {label}
            </Typography>
            <Typography sx={{ color: '#173042', fontWeight: 700, mt: 0.5, wordBreak: 'break-word' }}>
                {value || 'Not detected'}
            </Typography>
            {wasEdited && (
                <Typography variant="caption" sx={{ color: '#8a5e21', display: 'block', mt: 0.5 }}>
                    Edited after extraction. Original: {originalValue}
                </Typography>
            )}
        </Box>
    )
}

function FileResultsSection({ files, fileResults, sections, reviewState, showTechnicalLabels }) {
    const fileCards = buildFileResultsViewModel(files, fileResults, sections, reviewState)
    const [expandedFileId, setExpandedFileId] = useState(null)

    return (
        <Box>
            <Stack spacing={3}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>Results by file</Typography>

                {fileCards.map(file => {
                    const fileAccordionId = file.fileId || file.name

                    return (
                        <Accordion
                            key={fileAccordionId}
                            expanded={expandedFileId === fileAccordionId}
                            onChange={(_, isExpanded) => setExpandedFileId(isExpanded ? fileAccordionId : null)}
                            disableGutters
                            sx={{
                                border: '1px solid #e4e8eb',
                                borderRadius: '14px !important',
                                overflow: 'hidden',
                                boxShadow: 'none',
                                '&:before': {
                                    display: 'none'
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    px: 2.5,
                                    py: 1,
                                    '& .MuiAccordionSummary-content': {
                                        my: 0.5
                                    }
                                }}
                            >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }} sx={{ width: '100%', pr: 1 }}>
                                    <Box flex={1} minWidth={0}>
                                        <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                            {file.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {humanizeBundleLabel(file.bundleLabel)}
                                        </Typography>
                                    </Box>
                                    {showTechnicalLabels && !!file.issueFindings.length && (
                                        <Chip size="small" color="warning" label={`${file.issueFindings.length} issue${file.issueFindings.length === 1 ? '' : 's'}`} />
                                    )}
                                    {showTechnicalLabels && !!file.manualFindings.length && (
                                        <Chip size="small" variant="outlined" label={`${file.manualFindings.length} manual check${file.manualFindings.length === 1 ? '' : 's'}`} />
                                    )}
                                    {showTechnicalLabels && !!file.correctFindings.length && (
                                        <Chip size="small" color="success" variant="outlined" label={`${file.correctFindings.length} correct`} />
                                    )}
                                    {showTechnicalLabels && !!file.alerts?.length && (
                                        <Chip
                                            size="small"
                                            color={file.alerts.some(alert => alert.severity === 'error') ? 'error' : 'warning'}
                                            label={`${file.alerts.length} system alert${file.alerts.length === 1 ? '' : 's'}`}
                                        />
                                    )}
                                    {showTechnicalLabels && <Chip size="small" label={humanizeFileParseStatus(file)} color={file.supported ? 'success' : 'warning'} />}
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                {file.findings.length === 0 && !file.alerts?.length ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No checklist findings were generated for this file.
                                    </Typography>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {(file.alerts || []).map(alert => (
                                            <FileAlertSummary
                                                key={alert.id}
                                                alert={alert}
                                                showTechnicalLabels={showTechnicalLabels}
                                            />
                                        ))}

                                        {file.issueFindings.length > 0 && (
                                            <FindingGroup
                                                title="Issues to review"
                                                tone="issue"
                                                findings={file.issueFindings}
                                                showTechnicalLabels={showTechnicalLabels}
                                            />
                                        )}

                                        {file.correctFindings.length > 0 && (
                                            <FindingGroup
                                                title="No issue found"
                                                tone="correct"
                                                findings={file.correctFindings}
                                                showTechnicalLabels={showTechnicalLabels}
                                            />
                                        )}

                                        {file.manualFindings.length > 0 && (
                                            <FindingGroup
                                                title="Manual checks"
                                                tone="manual"
                                                findings={file.manualFindings}
                                                showTechnicalLabels={showTechnicalLabels}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )
                })}
            </Stack>
        </Box>
    )
}

function FileAlertSummary({ alert, showTechnicalLabels }) {
    const [dialogOpen, setDialogOpen] = useState(false)
    const detailPoints = buildAlertDetailPoints(alert)
    const shouldCollapse = detailPoints.length > 1

    return (
        <>
            <Alert
                severity={alertSeverity(alert.severity)}
                sx={{
                    borderRadius: 2,
                    alignItems: 'flex-start'
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        {alert.title}
                    </Typography>
                    {showTechnicalLabels && alert.confidenceLevel && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${humanizeConfidence(alert.confidenceLevel)} confidence`}
                            sx={{ alignSelf: 'flex-start' }}
                        />
                    )}
                </Stack>
                <Typography variant="body2">
                    {alert.summary}
                </Typography>

                {shouldCollapse ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="inherit">
                            {detailPoints.length} detail{detailPoints.length === 1 ? '' : 's'} available.
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setDialogOpen(true)}
                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, alignSelf: 'flex-start' }}
                        >
                            See details
                        </Button>
                    </Stack>
                ) : (
                    <Box sx={{ mt: detailPoints.length ? 1 : 0 }}>
                        {detailPoints.map((detail, index) => (
                            <Typography key={index} variant="body2" color="inherit">
                                - {detail}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Alert>

            <ReviewPointsDialog
                open={dialogOpen}
                title={alert.title || 'Details'}
                subtitle={alert.summary}
                points={detailPoints}
                onClose={() => setDialogOpen(false)}
            />
        </>
    )
}

function FindingGroup({ title, tone, findings, showTechnicalLabels }) {
    const [dialogOpen, setDialogOpen] = useState(false)
    const shouldCollapseManual = tone === 'manual' && findings.length > 1

    return (
        <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontWeight: 800, color: '#1b2c39' }}>
                    {title}
                </Typography>
                {showTechnicalLabels && (
                    <Chip
                        size="small"
                        variant="outlined"
                        color={tone === 'issue' ? 'warning' : tone === 'correct' ? 'success' : 'default'}
                        label={`${findings.length} item${findings.length === 1 ? '' : 's'}`}
                    />
                )}
            </Stack>

            {shouldCollapseManual ? (
                <>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: '#f8fafb',
                            border: '1px solid #e7edf1'
                        }}
                    >
                        <Stack spacing={1} alignItems="flex-start">
                            <Typography variant="body2" sx={{ color: '#334e5f', fontWeight: 700 }}>
                                {findings.length} manual check{findings.length === 1 ? '' : 's'} need reviewer confirmation.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                These are not automatic issues. Open the details when you are ready to review them.
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setDialogOpen(true)}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                            >
                                See details
                            </Button>
                        </Stack>
                    </Box>
                    <ManualFindingsDialog
                        open={dialogOpen}
                        title={title}
                        findings={findings}
                        showTechnicalLabels={showTechnicalLabels}
                        onClose={() => setDialogOpen(false)}
                    />
                </>
            ) : findings.map(finding => (
                <Box
                    key={`${finding.fileId}-${finding.itemId}`}
                    sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: tone === 'issue' ? '#fffaf4' : tone === 'correct' ? '#f4fbf6' : '#f8fafb',
                        border: tone === 'issue' ? '1px solid #f3ddba' : tone === 'correct' ? '1px solid #cfe8d7' : '1px solid #e7edf1'
                    }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
                        <Box flex={1}>
                            <Typography sx={{ fontWeight: 700 }}>
                                {finding.label}
                            </Typography>
                        </Box>
                        {showTechnicalLabels && (
                            <>
                                <Chip
                                    size="small"
                                    color={chipColorForStatus(finding.finalStatus)}
                                    label={statusLabel(finding.finalStatus)}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={finding.automationLabel || 'Manual review'}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${humanizeConfidence(finding.confidenceLevel)} confidence`}
                                />
                            </>
                        )}
                    </Stack>

                    {tone !== 'issue' && (
                        <Typography variant="body2" sx={{ mt: 1, color: '#334e5f', fontWeight: tone === 'correct' ? 700 : 400 }}>
                            {finding.summary}
                        </Typography>
                    )}

                    {!!finding.details?.length && (
                        <FindingDetailList details={finding.details} tone={tone} />
                    )}

                </Box>
            ))}
        </Stack>
    )
}

function ManualFindingsDialog({ open, title, findings, showTechnicalLabels, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ position: 'relative', pt: 5 }}>
                <DialogCloseButton onClose={onClose} />
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                {title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {findings.length} manual check{findings.length === 1 ? '' : 's'} for reviewer confirmation.
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack spacing={1.25}>
                        {findings.map(finding => (
                            <Box
                                key={`${finding.fileId}-${finding.itemId}`}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: '#f8fafb',
                                    border: '1px solid #e7edf1'
                                }}
                            >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
                                    <Box flex={1}>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {finding.label}
                                        </Typography>
                                    </Box>
                                    {showTechnicalLabels && (
                                        <>
                                            <Chip
                                                size="small"
                                                color={chipColorForStatus(finding.finalStatus)}
                                                label={statusLabel(finding.finalStatus)}
                                            />
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={finding.automationLabel || 'Manual review'}
                                            />
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${humanizeConfidence(finding.confidenceLevel)} confidence`}
                                            />
                                        </>
                                    )}
                                </Stack>

                                <Typography variant="body2" sx={{ mt: 1, color: '#334e5f' }}>
                                    {finding.summary}
                                </Typography>

                                {!!finding.details?.length && (
                                    <FindingDetailList details={finding.details} tone="manual" />
                                )}

                            </Box>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function FindingDetailList({ details, tone = 'manual' }) {
    const [detailDialog, setDetailDialog] = useState(null)

    return (
        <>
            <Stack spacing={1} sx={{ mt: 1 }}>
                {(details || []).map((detail, index) => {
                    const parsed = parseFindingDetail(detail)
                    const shouldCollapse = shouldCollapseDetail(parsed)
                    const summary = shouldCollapse ? summarizeLongDetail(parsed) : parsed

                    return (
                        <Box
                            key={index}
                            sx={{
                                p: 1.25,
                                borderRadius: 2,
                                backgroundColor: '#ffffff',
                                border: '1px solid #e6edf2'
                            }}
                        >
                            {summary.message && (
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                                    {summary.message}
                                </Typography>
                            )}

                            {summary.action && (
                                <Box
                                    sx={{
                                        mt: summary.message ? 1 : 0,
                                        p: 1,
                                        borderRadius: 1.5,
                                        backgroundColor: tone === 'issue' ? '#fff4e8' : '#f5f8fa',
                                        border: tone === 'issue' ? '1px solid #f1d6af' : '1px solid #e2e9ee'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#7a5a1a', display: 'block', mb: 0.25 }}>
                                        Action
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#334e5f', fontWeight: 600, lineHeight: 1.65 }}>
                                        {summary.action}
                                    </Typography>
                                </Box>
                            )}

                            {shouldCollapse && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setDetailDialog({ parsed, title: summary.dialogTitle })}
                                    sx={{ mt: 1, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                                >
                                    See details
                                </Button>
                            )}

                            {tone !== 'correct' && (parsed.location || parsed.preview) && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 1,
                                        borderRadius: 1.5,
                                        backgroundColor: '#f8fbfd',
                                        border: '1px solid #e2ebf0'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#5c7384', display: 'block', mb: 0.25 }}>
                                        {parsed.preview ? 'Currently using' : 'Where to look'}
                                    </Typography>
                                    {parsed.location && (
                                        <Typography variant="body2" color="text.secondary">
                                            {parsed.location}
                                        </Typography>
                                    )}
                                    {parsed.preview && (
                                        <Typography variant="body2" sx={{ mt: parsed.location ? 0.5 : 0, color: '#1f3646', fontStyle: 'italic' }}>
                                            “{parsed.preview}”
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )
                })}
            </Stack>

            <FindingDetailDialog
                open={Boolean(detailDialog)}
                detail={detailDialog}
                onClose={() => setDetailDialog(null)}
            />
        </>
    )
}

function FindingDetailDialog({ open, detail, onClose }) {
    const parsed = detail?.parsed || {}
    const points = buildDetailDialogPoints(parsed)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ position: 'relative', pt: 5 }}>
                <DialogCloseButton onClose={onClose} />
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                {detail?.title || 'Findings'}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack spacing={1}>
                        {points.map((point, index) => (
                            <Stack key={`${point}-${index}`} direction="row" spacing={1.25} alignItems="flex-start">
                                <Typography sx={{ minWidth: 28, fontWeight: 800, color: '#607789' }}>
                                    {index + 1}.
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#334e5f', lineHeight: 1.65 }}>
                                    {point}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function ReviewPointsDialog({ open, title, subtitle, points, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ position: 'relative', pt: 5 }}>
                <DialogCloseButton onClose={onClose} />
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                                {title || 'Details'}
                            </Typography>
                            {subtitle && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    <Stack spacing={1}>
                        {(points || []).map((point, index) => (
                            <Stack
                                key={`${point}-${index}`}
                                direction="row"
                                spacing={1.25}
                                alignItems="flex-start"
                                sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    backgroundColor: '#f8fbfd',
                                    border: '1px solid #e2ebf0'
                                }}
                            >
                                <Typography sx={{ minWidth: 28, fontWeight: 800, color: '#607789' }}>
                                    {index + 1}.
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#334e5f', lineHeight: 1.65 }}>
                                    {point}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function UploadCard({
    title,
    description,
    buttonLabel,
    appendButtonLabel,
    accept,
    multiple,
    allowAppend,
    selectedItems,
    onSelect,
    onAppend,
    onDropFiles,
    onClearAll
}) {
    const replaceInputRef = useRef(null)
    const appendInputRef = useRef(null)
    const [isDragging, setIsDragging] = useState(false)

    function handleInputChange(event, handler) {
        handler?.(Array.from(event.target.files || []))
        event.target.value = ''
    }

    function handleDragOver(event) {
        event.preventDefault()
        setIsDragging(true)
    }

    function handleDragLeave(event) {
        event.preventDefault()
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsDragging(false)
        }
    }

    function handleDrop(event) {
        event.preventDefault()
        setIsDragging(false)
        const droppedFiles = Array.from(event.dataTransfer?.files || [])
        onDropFiles?.(droppedFiles)
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                width: '100%',
                p: 2.5,
                height: '100%',
                borderRadius: 3,
                borderStyle: 'dashed',
                borderColor: isDragging ? '#2a7aa0' : '#bfd3df',
                backgroundColor: isDragging ? '#f0fbff' : '#fcfdff',
                transition: 'border-color 160ms ease, background-color 160ms ease'
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <Stack spacing={2}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box>
                        <Typography sx={{ fontWeight: 800, color: '#172d3d' }}>
                            {title}
                        </Typography>
                        {description && (
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Button
                        onClick={() => replaceInputRef.current?.click()}
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        sx={{
                            width: 'fit-content',
                            borderRadius: 2,
                            px: 2.25,
                            py: 1.1,
                            textTransform: 'none',
                            fontWeight: 700
                        }}
                    >
                        {buttonLabel}
                    </Button>
                    {selectedItems.length > 0 && onClearAll && (
                        <Button
                            color="inherit"
                            variant="text"
                            onClick={onClearAll}
                            sx={{ width: 'fit-content', textTransform: 'none', fontWeight: 700 }}
                        >
                            Clear this section
                        </Button>
                    )}
                </Stack>

                {allowAppend && selectedItems.length > 0 && (
                    <Button
                        onClick={() => appendInputRef.current?.click()}
                        variant="text"
                        startIcon={<UploadFileIcon />}
                        sx={{
                            width: 'fit-content',
                            textTransform: 'none',
                            fontWeight: 700,
                            color: '#1d6582'
                        }}
                    >
                        {appendButtonLabel || 'Add more files'}
                    </Button>
                )}

                <Typography variant="body2" color="text.secondary">
                    Drag and drop {multiple ? 'files' : 'a file'} here, or use the button{allowAppend ? 's' : ''} above.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1
                    }}
                >
                    {selectedItems.length === 0 && (
                        <Chip
                            label="Nothing uploaded yet"
                            variant="outlined"
                            sx={{ borderStyle: 'dashed', color: '#647786' }}
                        />
                    )}
                    {selectedItems.map(item => (
                        <Stack
                            key={item.key}
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={0.75}
                            alignItems={{ sm: 'center' }}
                            sx={{
                                maxWidth: '100%',
                                p: 0.75,
                                borderRadius: 2,
                                backgroundColor: '#f5f8fa',
                                border: '1px solid #d6e2ea'
                            }}
                        >
                            <Chip
                                label={item.label}
                                variant="outlined"
                                onDelete={item.onDelete}
                                sx={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderColor: '#d6e2ea',
                                    color: '#244253',
                                    fontWeight: 700,
                                    '& .MuiChip-deleteIcon': {
                                        color: '#6e8492',
                                        '&:hover': { color: '#2c4a5c' }
                                    },
                                    '& .MuiChip-label': {
                                        display: 'block',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.35,
                                        paddingTop: '2px',
                                        paddingBottom: '2px',
                                        overflowWrap: 'anywhere'
                                    }
                                }}
                            />
                            {item.onMove && (
                                <TextField
                                    select
                                    size="small"
                                    label="Category"
                                    value={item.currentBundleKey}
                                    onChange={(event) => item.onMove(event.target.value)}
                                    sx={{ minWidth: 170 }}
                                >
                                    <MenuItem value="compilation_fs">Financial statements</MenuItem>
                                    <MenuItem value="tax_computation">Tax computation</MenuItem>
                                    <MenuItem value="other_documents">Other documents</MenuItem>
                                </TextField>
                            )}
                        </Stack>
                    ))}
                </Box>

                <input
                    ref={replaceInputRef}
                    hidden
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={(event) => handleInputChange(event, onSelect)}
                />

                {allowAppend && (
                    <input
                        ref={appendInputRef}
                        hidden
                        type="file"
                        accept={accept}
                        multiple={multiple}
                        onChange={(event) => handleInputChange(event, onAppend)}
                    />
                )}
            </Stack>
        </Paper>
    )
}

export function SavedReviewsDialogContent({ reviews, clients, loading, selectedReviewId, onLoadReview, onOpenPrivacy, searchValue, onSearchChange }) {
    const [localSearchTerm, setLocalSearchTerm] = useState('')
    const searchTerm = searchValue === undefined ? localSearchTerm : searchValue
    const setSearchTerm = value => {
        setLocalSearchTerm(value)
        onSearchChange?.(value)
    }
    const [companyFilter, setCompanyFilter] = useState('all')
    const [statusFilter, setStatusFilter] = useState('all')
    const [page, setPage] = useState(1)
    const pageSize = 8
    const companyOptions = Array.from(new Set((reviews || []).map(review => reviewCompanyName(review, clients)).filter(Boolean))).sort((a, b) => a.localeCompare(b))
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const filteredReviews = (reviews || []).filter(review => {
        const companyName = reviewCompanyName(review, clients)
        const matchesCompany = companyFilter === 'all' || companyName === companyFilter
        const matchesStatus = statusFilter === 'all' || reviewWorkQueueStatus(review) === statusFilter
        const searchableText = [
            review.reviewId,
            reviewDisplayName(review, clients),
            review.recordName,
            ...(Array.isArray(review.fileNames) ? review.fileNames : []),
            formatDateTime(review.createdAt)
        ].join(' ').toLowerCase()

        return matchesCompany && matchesStatus && (onSearchChange || !normalizedSearch || searchableText.includes(normalizedSearch))
    }).sort((left, right) => {
        const leftDate = new Date(left.updatedAt || left.createdAt || 0).getTime()
        const rightDate = new Date(right.updatedAt || right.createdAt || 0).getTime()
        return rightDate - leftDate
    })
    const totalPages = Math.max(1, Math.ceil(filteredReviews.length / pageSize))
    const safePage = Math.min(page, totalPages)
    const visibleReviews = filteredReviews.slice((safePage - 1) * pageSize, safePage * pageSize)

    useEffect(() => {
        setPage(1)
    }, [searchTerm, companyFilter, statusFilter, reviews.length])

    return (
        <Box sx={{ p: { xs: 2.5, md: 3 }, backgroundColor: '#f7fafc' }}>
            <Stack spacing={2.5}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{
                        pb: 1.5,
                        borderBottom: '1px solid #e2eaf0'
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 850, color: '#1b2c39' }}>
                        Past reviews
                    </Typography>
                    {onOpenPrivacy && <Button
                        variant="text"
                        size="small"
                        startIcon={<InfoOutlinedIcon />}
                        onClick={onOpenPrivacy}
                    >
                        Privacy &amp; file handling
                    </Button>}
                </Stack>

                <Box>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.25}
                        alignItems={{ md: 'center' }}
                    >
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="Search company, file name or review ID"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            inputProps={{ maxLength: 160 }}
                            sx={{ flex: 1, minWidth: { md: 280 } }}
                        />
                        {companyOptions.length > 0 && <TextField
                                select
                                size="small"
                                label="Company"
                                value={companyFilter}
                                onChange={(event) => setCompanyFilter(event.target.value)}
                                sx={{ minWidth: { xs: '100%', md: 240 } }}
                            >
                                <MenuItem value="all">All companies</MenuItem>
                                {companyOptions.map(companyName => (
                                    <MenuItem key={companyName} value={companyName}>
                                        {companyName}
                                    </MenuItem>
                                ))}
                            </TextField>}
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 180 } }}
                        >
                            <MenuItem value="all">All statuses</MenuItem>
                            <MenuItem value="blocked">Check before sending</MenuItem>
                            <MenuItem value="open">Issues to review</MenuItem>
                            <MenuItem value="ready">Ready</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="failed">Could not complete</MenuItem>
                            <MenuItem value="legacy">Older result</MenuItem>
                        </TextField>
                        {(searchTerm || companyFilter !== 'all' || statusFilter !== 'all') && (
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setSearchTerm('')
                                    setCompanyFilter('all')
                                    setStatusFilter('all')
                                }}
                                sx={smallSettingsButtonSx}
                            >
                                Clear
                            </Button>
                        )}
                    </Stack>
                    {(searchTerm || companyFilter !== 'all' || statusFilter !== 'all') && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {filteredReviews.length} result{filteredReviews.length === 1 ? '' : 's'}
                    </Typography>}
                    {onSearchChange && reviews.length >= 50 && !loading && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Showing the latest 50 matching reviews. Search by company, period or file name to narrow the list.
                    </Typography>}
                </Box>

                {loading && (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, backgroundColor: '#ffffff' }}>
                        <Typography variant="body2" color="text.secondary">
                        Loading saved reviews…
                        </Typography>
                    </Paper>
                )}

                {!loading && filteredReviews.length === 0 && (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, backgroundColor: '#ffffff' }}>
                        <Typography variant="body2" color="text.secondary">
                            No saved reviews found for this search or filter.
                        </Typography>
                    </Paper>
                )}

                {!loading && visibleReviews.length > 0 && (
                    <Stack spacing={1.5}>
                        <Stack spacing={1.25}>
                        {visibleReviews.map(review => {
                            const isSelected = review.reviewId === selectedReviewId
                            const parsed = review.summary?.documentsParsed || 0
                            const uploaded = review.summary?.documentsUploaded || 0
                            const openActions = (review.resolutionSummary?.open ?? review.summary?.actionItems ?? 0) + (review.resolutionSummary?.needsClarification ?? 0)
                            const blockers = review.blockerSummary?.unresolved ?? review.summary?.suspectedWrongClientFiles ?? 0
                            const queueStatus = reviewWorkQueueStatus(review)
                            const displayName = reviewDisplayName(review, clients)
                            const createdLabel = formatDateTime(review.createdAt)
                            const fileSummary = parsed === uploaded
                                ? `${uploaded} file${uploaded === 1 ? '' : 's'} checked`
                                : `${parsed} of ${uploaded} files checked`
                            const issueSummary = `${openActions} issue${openActions === 1 ? '' : 's'}`

                            return (
                                <Paper
                                    key={review.reviewId}
                                    component="article"
                                    aria-label={`${displayName} review from ${createdLabel}`}
                                    aria-current={isSelected ? 'true' : undefined}
                                    variant="outlined"
                                    sx={{
                                        p: { xs: 1.75, md: 2 },
                                        borderRadius: 2.5,
                                        backgroundColor: '#ffffff',
                                        borderColor: isSelected ? '#6aa7df' : '#e1e9ef',
                                        boxShadow: isSelected ? '0 10px 28px rgba(31, 112, 180, 0.12)' : '0 8px 20px rgba(16, 24, 40, 0.035)'
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1.5}
                                        alignItems={{ md: 'center' }}
                                        justifyContent="space-between"
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
                                                <Typography sx={{ fontWeight: 850, color: '#1b2c39', wordBreak: 'break-word' }}>
                                                    {displayName}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    color={['blocked', 'failed'].includes(queueStatus) ? 'error' : queueStatus === 'ready' ? 'success' : queueStatus === 'processing' ? 'info' : 'default'}
                                                    variant={queueStatus === 'open' ? 'outlined' : 'filled'}
                                                    label={queueStatus === 'blocked'
                                                        ? 'Check before sending'
                                                        : queueStatus === 'ready'
                                                            ? 'Ready'
                                                            : queueStatus === 'legacy'
                                                                ? 'Older result'
                                                                : queueStatus === 'processing'
                                                                    ? 'Processing'
                                                                    : queueStatus === 'failed'
                                                                        ? 'Could not complete'
                                                                        : 'Issues to review'}
                                                />
                                            </Stack>
                                            {review.recordName && review.recordName !== displayName && (
                                                <Typography variant="body2" sx={{ mb: 0.75, color: '#526b7b', overflowWrap: 'anywhere' }}>
                                                    {review.recordName}
                                                </Typography>
                                            )}
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="body2" color="text.secondary">
                                                    {createdLabel}
                                                </Typography>
                                                {review.createdBy?.name && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        by {review.createdBy.name}
                                                    </Typography>
                                                )}
                                                <SavedReviewSourceStatus review={review} />
                                            </Stack>
                                            <Typography variant="body2" sx={{ mt: 0.8, color: '#526b7b', fontWeight: 700 }}>
                                                {fileSummary} · {issueSummary}{blockers > 0 ? ` · ${blockers} file${blockers === 1 ? ' needs' : 's need'} attention` : ''}
                                            </Typography>
                                        </Box>

                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        >
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<FactCheckIcon />}
                                                onClick={() => onLoadReview(review.reviewId)}
                                                aria-label={`View ${displayName} from ${createdLabel}`}
                                                sx={smallSettingsButtonSx}
                                            >
                                                View
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            )
                        })}
                        </Stack>

                        {totalPages > 1 && (
                            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                                <Pagination
                                    count={totalPages}
                                    page={safePage}
                                    onChange={(_, value) => setPage(value)}
                                    color="primary"
                                    shape="rounded"
                                />
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        </Box>
    )
}

function reviewCompanyName(review, clients = []) {
    const linkedClient = (clients || []).find(client => String(client?._id || '') === String(review?.clientId || ''))
    return linkedClient?.clientInfo?.companyName || review?.companyName || ''
}

function reviewDisplayName(review, clients = []) {
    return reviewCompanyName(review, clients) || review?.recordName ||
        (review?.reviewId ? `Review ${String(review.reviewId).slice(0, 8)}` : 'Saved review')
}

function ChecklistSection({ sections, showTechnicalLabels }) {
    const [expandedChecklistItemId, setExpandedChecklistItemId] = useState(null)

    return (
        <Box>
            <Stack spacing={3}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b2c39' }}>
                    Checklist results
                </Typography>

                {(sections || []).map(section => {
                    const visibleItems = (section.items || []).filter(item => item.suggestedStatus !== 'na' && item.finalStatus !== 'na')
                    if (!visibleItems.length) return null

                    return (
                        <Box key={section.key}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {sentenceCaseTitle(section.title)}
                                </Typography>
                                {section.subtitle && (
                                    <Tooltip
                                        title={section.subtitle}
                                        arrow
                                        componentsProps={{
                                            tooltip: {
                                                sx: tooltipReadableSx
                                            }
                                        }}
                                    >
                                        <IconButton aria-label={`About ${sentenceCaseTitle(section.title)}`} size="small" sx={{ p: 0.25, color: '#6d8493' }}>
                                            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>

                            <Stack spacing={2}>
                                {visibleItems.map(item => {
                                    const displayStatus = checklistSuggestedStatus(item)
                                    const checklistAccordionId = `${section.key || 'section'}-${item.id || item.label}`

                                    return (
                                        <Accordion
                                            key={checklistAccordionId}
                                            expanded={expandedChecklistItemId === checklistAccordionId}
                                            onChange={(_, isExpanded) => setExpandedChecklistItemId(isExpanded ? checklistAccordionId : null)}
                                            disableGutters
                                            sx={{
                                                border: '1px solid #e4e8eb',
                                                borderRadius: '14px !important',
                                                overflow: 'hidden',
                                                boxShadow: 'none',
                                                '&:before': {
                                                    display: 'none'
                                                }
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    '& .MuiAccordionSummary-content': {
                                                        my: 0.5
                                                    }
                                                }}
                                            >
                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ width: '100%', pr: 1 }}>
                                                    <Stack direction="row" spacing={1.25} alignItems="flex-start" flex={1} minWidth={0}>
                                                        <ChecklistStatusIcon status={displayStatus} />
                                                        <Box flex={1} minWidth={0}>
                                                            <Typography fontWeight={700}>{item.label}</Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {displayStatus === 'manual' && item.suggestedStatus === 'pass' ? 'No issue detected automatically. Manual verification is still required.' : item.summary}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>

                                                    {showTechnicalLabels && item.autoFixCapable && (
                                                        <Chip size="small" color="info" label="Auto-fixable" />
                                                    )}

                                                    {showTechnicalLabels && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={item.automationLabel || 'Manual review'}
                                                        />
                                                    )}

                                                    {showTechnicalLabels && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={`${humanizeConfidence(item.confidenceLevel)} confidence`}
                                                        />
                                                    )}

                                                    <Chip
                                                        size="small"
                                                        color={chipColorForStatus(displayStatus)}
                                                        label={staffFacingStatusLabel(displayStatus)}
                                                    />

                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                                                <ChecklistItemDetails item={item} />
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                })}
                            </Stack>
                        </Box>
                    )
                })}
            </Stack>
        </Box>
    )
}

function ChecklistItemDetails({ item }) {
    const tone = checklistSuggestedStatus(item) === 'pass'
        ? 'correct'
        : item.suggestedStatus === 'warning' || item.suggestedStatus === 'fail'
            ? 'issue'
            : 'manual'
    const details = item.details || []

    if (tone === 'issue') {
        const issueDetails = details.filter(detail => isIssueFindingDetail(detail))
        const displayDetails = issueDetails.length ? issueDetails : details

        return <FindingDetailList details={displayDetails} tone={tone} />
    }

    return <FindingDetailList details={details} tone={tone} />
}

function ChecklistStatusIcon({ status }) {
    if (status === 'pass') {
        return <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22, mt: 0.1 }} />
    }

    if (status === 'warning' || status === 'fail') {
        return <ErrorOutlineIcon sx={{ color: status === 'fail' ? '#c62828' : '#ed6c02', fontSize: 22, mt: 0.1 }} />
    }

    return <HelpOutlineIcon sx={{ color: '#78909c', fontSize: 22, mt: 0.1 }} />
}

function ReviewerFeedbackEditor({ title, value, options, onDecisionChange, onNoteChange, sx = {} }) {
    return (
        <Box sx={sx}>
            <Stack spacing={1}>
                <Typography variant="caption" sx={{ color: '#5f7585', fontWeight: 700, letterSpacing: 0.3 }}>
                    {title}
                </Typography>
                <TextField
                    select
                    size="small"
                    value={value?.decision || ''}
                    onChange={(event) => onDecisionChange?.(event.target.value)}
                >
                    {options.map(option => (
                        <MenuItem key={option.value || 'empty'} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    placeholder="Optional reviewer note"
                    value={value?.note || ''}
                    onChange={(event) => onNoteChange?.(event.target.value)}
                    multiline
                    minRows={2}
                />
            </Stack>
        </Box>
    )
}

function buildInitialReviewState(sections) {
    const output = {}

    for (const section of sections || []) {
        for (const item of section.items || []) {
            output[item.id] = {
                finalStatus: item.finalStatus || item.suggestedStatus
            }
        }
    }

    return output
}

function mergeFiles(existingFiles, nextFiles) {
    const output = [...(existingFiles || [])]
    const seen = new Set(output.map(file => fileKey(file)))

    for (const file of nextFiles || []) {
        const key = fileKey(file)
        if (seen.has(key)) continue
        seen.add(key)
        output.push(file)
    }

    return output
}

function fileKey(file) {
    return `${file?.name || 'file'}-${file?.size || 0}-${file?.lastModified || 0}`
}

const selectorInputSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        width: '100%',
        minHeight: 76,
        alignItems: 'flex-start',
        borderRadius: 2.5,
        backgroundColor: '#fbfdff',
        '& input': {
            fontSize: '1rem',
            fontWeight: 600,
            paddingTop: '18px'
        }
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 0
    }
}

const selectorAutocompleteSx = {
    width: '100%',
    maxWidth: 'none',
    minWidth: 0,
    display: 'block',
    flex: '1 1 100%',
    flexBasis: '100%',
    '& .MuiFormControl-root': {
        width: '100%',
        maxWidth: 'none'
    },
    '& .MuiOutlinedInput-root': {
        width: '100%',
        maxWidth: 'none'
    },
    '& .MuiAutocomplete-inputRoot': {
        width: '100% !important'
    }
}

const clientFieldRowSx = {
    width: '100%',
    maxWidth: 'none',
    minWidth: 0,
    display: 'block',
    flex: '1 1 100%'
}

const overrideFieldSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        width: '100%'
    }
}

const manualOverrideGridSx = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))'
    },
    gap: 2
}

const manualOverrideCellSx = {
    width: '100%',
    minWidth: 0
}

const financialPeriodSetupSx = {
    width: '100%',
    border: '1px solid #dbe6ec',
    borderRadius: 2,
    backgroundColor: '#fbfcfd',
    p: { xs: 2, md: 2.25 }
}

const financialPeriodGridSx = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(3, minmax(0, 1fr))'
    },
    gap: 2
}

const requiredChipSx = {
    fontWeight: 800,
    color: '#36566d',
    backgroundColor: '#eef5f8',
    border: '1px solid #d8e5eb'
}

const bizfileUploadStepGridSx = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: {
        xs: '1fr',
        lg: 'minmax(320px, 520px) minmax(280px, 1fr)'
    },
    gap: 2,
    alignItems: 'start',
    justifyItems: 'stretch'
}

const privacyAssuranceSx = {
    gridColumn: '1 / -1',
    width: '100%',
    p: { xs: 1.75, md: 2 },
    borderRadius: 2.5,
    borderColor: '#beddd2',
    backgroundColor: '#f3fbf7'
}

const privacyAssuranceIconSx = {
    width: 38,
    height: 38,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#1f665a',
    backgroundColor: '#dff2e9'
}

const privacyDetailsButtonSx = {
    mt: 0.75,
    px: 0,
    minWidth: 0,
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontWeight: 800,
    color: '#1f665a'
}

const sourceConfirmationSx = {
    width: '100%',
    border: 0,
    backgroundColor: 'transparent',
    p: 0
}

const sourceConfirmationGridSx = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))'
    },
    gap: 2
}

const sourceAlertSx = {
    borderRadius: 2,
    '& .MuiAlert-message': {
        lineHeight: 1.55
    }
}

const extractBizfileButtonSx = {
    borderRadius: 2,
    px: 2.25,
    py: 1.05,
    textTransform: 'none',
    fontWeight: 800,
    whiteSpace: 'nowrap'
}

const uploadBundlesGridSx = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))'
    },
    gap: 2.5,
    alignItems: 'stretch',
    justifyItems: 'stretch'
}

const uploadBundleCellSx = {
    width: '100%',
    minWidth: 0,
    display: 'flex'
}

const selectorOptionSx = {
    display: 'block',
    py: 1.25,
    px: 1.5,
    borderBottom: '1px solid #eef2f4',
    alignItems: 'flex-start !important'
}

const advancedOverridesSx = {
    border: '1px solid #e2e9ee',
    borderRadius: 3,
    backgroundColor: '#fbfcfd',
    p: 2.25
}

const heroActionButtonSx = {
    borderRadius: 2.2,
    px: 2.25,
    py: 1.25,
    textTransform: 'none',
    fontWeight: 700,
    whiteSpace: 'nowrap'
}

const smallSettingsButtonSx = {
    borderRadius: 2,
    px: 1.5,
    py: 0.75,
    minWidth: 'auto',
    alignSelf: { xs: 'flex-start', md: 'center' },
    textTransform: 'none',
    fontWeight: 700,
    whiteSpace: 'nowrap'
}

const tooltipReadableSx = {
    fontSize: '0.92rem',
    lineHeight: 1.55,
    px: 1.5,
    py: 1.1,
    maxWidth: 340,
    backgroundColor: '#172633',
    color: '#ffffff',
    boxShadow: '0 10px 24px rgba(15, 35, 50, 0.26)'
}

const runReviewButtonSx = {
    borderRadius: 2.25,
    minWidth: { sm: 190 },
    px: 3,
    py: 1.25,
    textTransform: 'none',
    fontWeight: 800,
    boxShadow: 'none'
}

const resultsMenuButtonSx = (active) => ({
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: '12px 12px 0 0',
    px: 2,
    py: 1.1,
    color: active ? '#0f5fa8' : '#587082',
    backgroundColor: active ? '#ffffff' : 'transparent',
    border: '1px solid',
    borderColor: active ? '#dbe7ef' : 'transparent',
    borderBottomColor: active ? '#ffffff' : 'transparent',
    mb: '-1px',
    '&:hover': {
        backgroundColor: active ? '#ffffff' : '#eef5f9',
        borderColor: active ? '#dbe7ef' : 'transparent',
        borderBottomColor: active ? '#ffffff' : 'transparent'
    }
})

const roundedAccordionSx = {
    border: '1px solid #e4e8eb',
    borderRadius: '14px !important',
    overflow: 'hidden',
    boxShadow: 'none',
    '&:before': {
        display: 'none'
    }
}

const documentAccordionSummarySx = {
    px: 2.5,
    py: 1,
    '& .MuiAccordionSummary-content': {
        my: 0.5
    }
}

const journeyStepLabelSx = {
    color: '#537185',
    fontWeight: 800,
    letterSpacing: 1.1,
    fontSize: '0.78rem',
    lineHeight: 1.45,
    display: 'block',
    mb: 0.2
}

const journeyStepTitleSx = {
    fontWeight: 800,
    color: '#1b2c39',
    fontSize: { xs: '1.05rem', md: '1.1rem' },
    lineHeight: 1.4
}

function buildDirectorPrefill(client) {
    const names = []
    const candidates = [
        client?.clientInfo?.auth1Name,
        client?.clientInfo?.auth2Name,
        client?.clientInfo?.auth3Name,
        client?.clientInfo?.auth4Name
    ]

    for (const candidate of candidates) {
        if (candidate && !names.includes(candidate)) {
            names.push(candidate)
        }
    }

    return names.join(', ')
}

function compactAddress(value) {
    return String(value || '').replace(/\s+/g, ' ').trim()
}

function normalizeFye(value) {
    return value == null ? '' : String(value)
}

function toDateInputValue(value) {
    if (!value) return ''
    const text = String(value).trim()
    const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`

    const slashDate = text.match(/\b([0-9]{1,2})[./-]([0-9]{1,2})[./-]([0-9]{4})\b/)
    if (slashDate) {
        return formatDateInputParts(Number(slashDate[3]), Number(slashDate[2]), Number(slashDate[1]))
    }

    const dayMonthYear = text.match(/\b([0-9]{1,2})\s+([A-Za-z]{3,9})\.?\s+([0-9]{4})\b/)
    if (dayMonthYear) {
        return formatDateInputParts(Number(dayMonthYear[3]), monthNameToNumber(dayMonthYear[2]), Number(dayMonthYear[1]))
    }

    const monthDayYear = text.match(/\b([A-Za-z]{3,9})\.?\s+([0-9]{1,2}),?\s+([0-9]{4})\b/)
    if (monthDayYear) {
        return formatDateInputParts(Number(monthDayYear[3]), monthNameToNumber(monthDayYear[1]), Number(monthDayYear[2]))
    }

    return ''
}

function formatDateInputForDocument(value) {
    const dateInput = toDateInputValue(value)
    if (!dateInput) return ''

    const [, year, month, day] = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/) || []
    const monthName = MONTH_NAMES[Number(month) - 1]
    return monthName ? `${Number(day)} ${monthName} ${year}` : ''
}

function deriveYaFromDateInput(value) {
    const dateInput = toDateInputValue(value)
    const match = dateInput.match(/^(\d{4})-/)
    return match ? String(Number(match[1]) + 1) : ''
}

function deriveFinancialPeriodPresetYear(expectations = {}) {
    const end = toDateInputValue(expectations.expectedFinancialPeriodEnd || expectations.expectedFye || '')
    const endYear = Number((end.match(/^(\d{4})-/) || [])[1])
    if (endYear) return endYear

    const ya = Number(String(expectations.expectedYa || '').replace(/^YA\s*/i, '').trim())
    if (ya && ya > 1900) return ya - 1

    return new Date().getFullYear() - 1
}

function buildFinancialPeriodPresets(year) {
    const safeYear = Number(year) || new Date().getFullYear() - 1
    return [
        {
            key: `calendar-${safeYear}`,
            label: `1 Jan - 31 Dec ${safeYear}`,
            start: `${safeYear}-01-01`,
            end: `${safeYear}-12-31`
        },
        {
            key: `jul-jun-${safeYear}`,
            label: `1 Jul ${safeYear - 1} - 30 Jun ${safeYear}`,
            start: `${safeYear - 1}-07-01`,
            end: `${safeYear}-06-30`
        },
        {
            key: `apr-mar-${safeYear}`,
            label: `1 Apr ${safeYear - 1} - 31 Mar ${safeYear}`,
            start: `${safeYear - 1}-04-01`,
            end: `${safeYear}-03-31`
        }
    ]
}

function isDateInputRangeInvalid(start, end) {
    return Boolean(start && end && start > end)
}

function formatDateInputParts(year, month, day) {
    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) return ''
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function monthNameToNumber(value) {
    const normalized = String(value || '').trim().slice(0, 3).toLowerCase()
    return MONTH_NAMES.findIndex(month => month.slice(0, 3).toLowerCase() === normalized) + 1
}

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
]

function formatBytes(bytes) {
    if (!bytes) return '0 B'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDateTime(value) {
    if (!value) return 'n/a'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'n/a'
    return date.toLocaleString()
}

function humanizeBundleLabel(value) {
    return value || 'Document bundle'
}

function sentenceCaseTitle(value) {
    const text = String(value || '').trim()
    if (!text) return ''

    const lowered = text
        .toLowerCase()
        .replace(/\bfs\b/g, 'FS')
        .replace(/\bbizfile\b/g, 'BizFile')
        .replace(/\bfmd\b/g, 'FMD')
        .replace(/\buen\b/g, 'UEN')

    return `${lowered.charAt(0).toUpperCase()}${lowered.slice(1)}`
}

function humanizeFileParseStatus(file) {
    if (file?.supported && file?.parseStatus === 'parsed') {
        return 'Ready for review'
    }

    if (!file?.supported || file?.parseStatus === 'unsupported') {
        return 'Manual review needed'
    }

    return humanizeKey(file?.parseStatus || 'status')
}

function buildFeedbackCounts(feedbackState) {
    return {
        findings: Object.values(feedbackState?.findings || {}).filter(entry => entry?.decision || entry?.note).length,
        corrections: Object.values(feedbackState?.corrections || {}).filter(entry => entry?.decision || entry?.note).length
    }
}

function buildFeedbackFindingItems(result, reviewState) {
    const files = buildFileResultsViewModel(result?.files || [], result?.fileResults || [], result?.sections || [], reviewState || {})
    return files.flatMap(file =>
        (file.findings || [])
            .filter(finding => finding.group !== 'correct')
            .map(finding => ({
            id: finding.feedbackId,
            title: finding.label,
            fileName: file.name,
            statusLabel: feedbackFindingStatusLabel(finding),
            summary: buildFeedbackFindingSummary(finding)
            }))
    ).slice(0, 60)
}

function feedbackFindingStatusLabel(finding) {
    if (finding.group === 'issue') return 'Issue'
    if (finding.group === 'correct') return 'Correct'
    return 'Manual check'
}

function buildFeedbackFindingSummary(finding) {
    const detail = (finding.details || []).find(value => !isContextOnlySummaryText(value)) || ''
    const parsed = detail ? parseFindingDetail(detail) : null
    return parsed
        ? [parsed.message, parsed.action].filter(Boolean).join(' ') || finding.summary
        : finding.summary
}

function buildFindingFeedbackId(fileId, itemId) {
    return `finding:${fileId}:${itemId}`
}

function stripSummaryFilePrefix(detail) {
    const value = String(detail || '').trim()
    const colonIndex = value.indexOf(':')
    if (colonIndex <= 0) return value

    const possiblePrefix = value.slice(0, colonIndex)
    if (/\.(docx?|xlsx?|pdf)$/i.test(possiblePrefix)) {
        return value.slice(colonIndex + 1).trim()
    }

    return value
}

function isContextOnlySummaryText(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim().toLowerCase()
    if (!text) return true

    const contextOnlyPatterns = [
        /^compared against the dominant values detected across the uploaded documents in this review pack\.?$/,
        /^this file does not fully align with the rest of the uploaded review pack\.?$/,
        /^confirm this file belongs to the same final document set and update the inconsistent values if needed\.?$/,
        /^please confirm this file belongs to the same final document set and update the inconsistent values if needed\.?$/,
        /^normalize equivalent headings to a consistent style\./,
        /^major headings appear to use mixed paragraph formatting\./,
        /^confirm the final workbook prints fully without cut-off or hidden statement columns\./,
        /^workbook layout \/ print-preview issue detected\./,
        /^review the final rendered document or print preview before issue/
    ]

    return contextOnlyPatterns.some(pattern => pattern.test(text))
}

function buildFileResultsViewModel(files, fileResults, sections, reviewState) {
    const items = (sections || []).flatMap(section =>
        (section.items || []).map(item => ({
            ...item,
            sectionTitle: section.title,
            finalStatus: checklistFinalStatus(item, reviewState?.[item.id]),
            automationClass: item.automationClass || 'manual_review',
            automationLabel: item.automationLabel || 'Manual review',
            confidenceLevel: item.confidenceLevel || 'low'
        }))
    )
    const fileResultMap = new Map((fileResults || []).map(file => [file.fileId, file]))

    return (files || []).map(file => {
        const findings = items
            .filter(item => (item.applicableFileIds || []).includes(file.id))
            .map(item => buildFileScopedFinding(item, file))
        const issueFindings = findings.filter(finding => finding.group === 'issue')
        const manualFindings = findings.filter(finding => finding.group === 'manual')
        const correctFindings = findings.filter(finding => finding.group === 'correct')

        return {
            ...file,
            alerts: fileResultMap.get(file.id)?.alerts || [],
            fileAlerts: fileResultMap.get(file.id)?.alerts || [],
            findings,
            issueFindings,
            manualFindings,
            correctFindings
        }
    })
}

function buildFileScopedFinding(item, file) {
    const specificDetails = (item.details || [])
        .filter(detail => isDetailForFile(detail, file.name))
        .map(detail => stripFilePrefix(detail, file.name))

    const genericDetails = (item.details || []).filter(detail =>
        !referencesAnyFile(detail, item.applicableFiles || [])
    )

    const isAffected = (item.affectedFileIds || []).includes(file.id)
    const fileSummary = buildFileScopedSummary(item, file, specificDetails, isAffected)
    const group = classifyFileFindingGroup(item, {
        specificDetails,
        genericDetails,
        isAffected
    })
    const headline = buildFileFindingHeadline(item, isAffected, group)
    const details = specificDetails.length
        ? specificDetails.slice(fileSummary === specificDetails[0] ? 1 : 0)
        : genericDetails

    return {
        ...item,
        isAffected,
        group,
        feedbackId: buildFindingFeedbackId(file.id, item.id),
        summary: headline,
        details: fileSummary && fileSummary !== headline ? [fileSummary, ...details] : details
    }
}

function classifyFileFindingGroup(item, { specificDetails = [], genericDetails = [], isAffected = false } = {}) {
    if (item.finalStatus === 'pass') return 'correct'

    if (specificDetails.length) {
        const hasIssueDetail = specificDetails.some(detail => isIssueFindingDetail(detail))
        const hasPositiveDetail = specificDetails.some(detail => isPositiveFindingDetail(detail))

        if (hasIssueDetail || isAffected) return 'issue'
        if (hasPositiveDetail && checklistSuggestedStatus(item) !== 'manual') return 'correct'
    }

    if (isAffected) return 'issue'
    if (genericDetails.some(detail => isIssueFindingDetail(detail))) return 'issue'
    if (item.finalStatus === 'warning' || item.finalStatus === 'fail') return 'manual'
    return 'manual'
}

function buildFileFindingHeadline(item, isAffected, group) {
    if (group === 'correct' || item.finalStatus === 'pass') {
        return filePassHeadline(item.id)
    }

    if (group === 'issue' || isAffected) {
        return fileIssueHeadline(item.id)
    }

    if (item.finalStatus === 'na') {
        return 'Not applicable to this file.'
    }

    return fileManualHeadline(item.id)
}

function filePassHeadline(itemId) {
    const map = {
        common_uen: 'Correctly detected: UEN matches the BizFile.',
        common_company_name: 'Correctly detected: company name matches the BizFile.',
        common_registered_address: 'Correctly detected: registered address matches the BizFile.',
        common_directors: 'Correctly detected: director names match the BizFile.',
        common_fye: 'Correctly detected: financial year-end matches the source of truth.',
        common_excel_missing_lines: 'No hidden-row issue or missing expected tab was detected in this workbook.',
        fs_contents_page: 'No contents-page mismatch was detected automatically.',
        fs_two_directors: 'Director-signoff structure appears consistent with the BizFile.',
        fs_report_year: 'Correctly detected: report year matches the source of truth.',
        fs_page_numbering: 'No obvious page-numbering issue was detected automatically.',
        fs_notes_sequence: 'Notes sequence appears correct based on the extracted structure.',
        fs_net_assets: 'No net-assets inconsistency was detected automatically.',
        fs_same_date: 'Comparable documents use the same governing date.',
        tax_ya: 'Correctly detected: YA matches the source of truth.',
        tax_alignment: 'No obvious print-layout issue was detected automatically.',
        tax_schedule_sequence: 'No obvious tax schedule numbering issue was detected automatically.'
    }

    return map[itemId] || 'Correctly detected for this file.'
}

function fileIssueHeadline(itemId) {
    const map = {
        common_uen: 'UEN needs correction or is missing in this file.',
        common_company_name: 'Company name needs correction in this file.',
        common_registered_address: 'Registered address needs correction in this file.',
        common_directors: 'Director names need correction in this file.',
        common_fye: 'Financial year-end needs correction in this file.',
        common_excel_missing_lines: 'Possible hidden-row or missing-tab issue detected in this workbook.',
        fs_contents_page: 'Contents-page references need review in this file.',
        fs_two_directors: 'Director sign-off wording or signer count needs review.',
        fs_report_year: 'Report year needs correction in this file.',
        fs_page_numbering: 'Possible page-numbering issue detected in this file.',
        fs_notes_sequence: 'Notes sequencing needs correction in this file.',
        fs_net_assets: 'Possible balance-sheet inconsistency detected in this file.',
        fs_same_date: 'Document date does not align with related files.',
        tax_ya: 'YA needs correction in this file.',
        tax_alignment: 'Workbook print/layout needs review in this file.',
        tax_schedule_sequence: 'Tax schedule numbering needs review in this file.'
    }

    return map[itemId] || 'This file was flagged for this check.'
}

function fileManualHeadline(itemId) {
    const map = {
        common_excel_missing_lines: 'Manual confirmation is still required for workbook line continuity.',
        fs_contents_page: 'Manual confirmation is still required for contents-page mapping.',
        fs_two_directors: 'Manual confirmation is still required for director sign-off structure.',
        fs_report_year: 'Manual confirmation is still required for the report year.',
        fs_page_numbering: 'Manual confirmation is still required for final pagination.',
        fs_notes_sequence: 'Manual confirmation is still required for notes sequencing.',
        fs_net_assets: 'Manual confirmation is still required for the balance-sheet comparison.',
        fs_same_date: 'Manual confirmation is still required for document-date alignment.',
        tax_alignment: 'Manual confirmation is still required for workbook print layout.'
    }

    return map[itemId] || 'Manual confirmation is still required for this file.'
}

function isPositiveFindingDetail(detail) {
    const value = stripSummaryFilePrefix(detail).toLowerCase()
    if (!value) return false

    const positivePatterns = [
        /matches the bizfile/,
        /matches the source of truth/,
        /same value was detected/,
        /same values? (?:was|were) detected/,
        /was detected in this document/,
        /were detected in this document/,
        /appears consistent/,
        /appears correct/,
        /appears to be correct/,
        /no issue detected/,
        /no obvious .* issue was detected/,
        /no .* mismatch was detected/,
        /no .* inconsistency was detected/,
        /no .* problem was detected/,
        /no hidden-row issue/,
        /no amendment-style findings/
    ]

    return positivePatterns.some(pattern => pattern.test(value))
}

function isIssueFindingDetail(detail) {
    const value = stripSummaryFilePrefix(detail).toLowerCase()
    if (!value || isPositiveFindingDetail(value)) return false

    const issuePatterns = [
        /\bneeds correction\b/,
        /\bneeds review\b/,
        /\bmanual review is required\b/,
        /\bmanual review required\b/,
        /\bmissing\b/,
        /\binconsistent\b/,
        /\bmismatch\b/,
        /\bwrong\b/,
        /\bincorrect\b/,
        /\bdiffers?\b/,
        /\bdoes not\b/,
        /\bnot detected\b/,
        /\bno matching\b/,
        /\bbut no\b/,
        /\bissue detected\b/,
        /\bproblem detected\b/,
        /\bflagged\b/,
        /^\s*please\s+(?:amend|change|correct|insert|review|align|update|normalise|normalize|hide|move|add)\b/
    ]

    return issuePatterns.some(pattern => pattern.test(value))
}

function buildFileScopedSummary(item, file, specificDetails, isAffected) {
    if (specificDetails.length) {
        return specificDetails[0]
    }

    if (isAffected) {
        return item.finalStatus === 'pass'
            ? 'This file passed the check.'
            : 'This file was flagged for this check.'
    }

    if (item.finalStatus === 'pass') {
        return 'No issue detected for this file.'
    }

    if (item.finalStatus === 'manual' || item.finalStatus === 'na') {
        return 'Manual review still applies to this file.'
    }

    return 'This check ran for the file.'
}

function isDetailForFile(detail, fileName) {
    return String(detail || '').startsWith(`${fileName}:`)
}

function stripFilePrefix(detail, fileName) {
    return String(detail || '').replace(new RegExp(`^${escapeRegExp(fileName)}:\\s*`), '').trim()
}

function referencesAnyFile(detail, fileNames) {
    const value = String(detail || '')
    return (fileNames || []).some(fileName => value.includes(fileName))
}

function parseFindingDetail(detail) {
    let value = String(detail || '').trim()
    if (!value) {
        return { message: '', action: '', location: '', preview: '' }
    }

    let location = ''
    let preview = ''
    const whereIndex = value.indexOf('Where to look:')
    if (whereIndex >= 0) {
        const whereText = value.slice(whereIndex).trim()
        value = value.slice(0, whereIndex).trim()
        const parsedWhere = parseWhereToLook(whereText)
        location = parsedWhere.location
        preview = parsedWhere.preview
    }

    let action = ''
    const actionIndex = value.indexOf(' Please ')
    if (actionIndex >= 0) {
        action = value.slice(actionIndex + 1).trim()
        value = value.slice(0, actionIndex).trim()
    } else if (value.startsWith('Please ')) {
        action = value
        value = ''
    }

    return {
        message: value,
        action,
        location,
        preview
    }
}

function shouldCollapseDetail(parsed) {
    const text = [parsed.message, parsed.action].filter(Boolean).join(' ')
    return text.length > 360 || countOccurrences(text, ';') > 4 || countOccurrences(text, ',') > 22
}

function summarizeLongDetail(parsed) {
    const points = buildDetailDialogPoints(parsed)
    const subject = detectDetailSubject(parsed)

    return {
        message: parsed.message ? conciseHumanText(parsed.message) : '',
        action: `This section contains ${points.length} finding${points.length === 1 ? '' : 's'}. Click “See details” to review them.`,
        dialogTitle: subject
    }
}

function detectDetailSubject(parsed) {
    const text = `${parsed.message || ''} ${parsed.action || ''}`
    if (/tax schedule numbering/i.test(text)) return 'Tax schedule numbering details'
    if (/print settings|print area|layout|alignment/i.test(text)) return 'Print / layout details'
    return 'Findings'
}

function buildDetailDialogPoints(parsed) {
    const sections = []
    if (parsed.message) sections.push(parsed.message)
    if (parsed.action) sections.push(parsed.action.replace(/^Please\s+/i, ''))
    if (parsed.location) sections.push(`Where to look: ${parsed.location}`)
    if (parsed.preview) sections.push(`Currently using: ${parsed.preview}`)

    return sections
        .flatMap(splitLongDetailText)
        .map(item => capitalizeSentenceStart(item.trim()))
        .filter(Boolean)
}

function buildAlertDetailPoints(alert) {
    return (alert?.details || [])
        .filter(detail => !isContextOnlySummaryText(detail))
        .flatMap(detail => buildDetailDialogPoints(parseFindingDetail(detail)))
        .map(point => point.replace(/^[-\s]+/, '').trim())
        .filter(Boolean)
}

function splitLongDetailText(value) {
    const text = String(value || '').trim()
    if (!text) return []

    const markerIndex = text.indexOf('Detected markers:')
    const beforeMarkers = markerIndex >= 0 ? text.slice(0, markerIndex).trim() : text
    const markerText = markerIndex >= 0 ? text.slice(markerIndex + 'Detected markers:'.length).trim() : ''

    const points = splitDetailSegment(beforeMarkers)
    if (markerText) {
        points.push(...markerText.split(',').map(item => `Detected marker: ${item.trim()}`).filter(item => item !== 'Detected marker:'))
    }

    return points
}

function splitDetailSegment(value) {
    return String(value || '')
        .split(';')
        .flatMap(part => splitLongSentence(part))
        .map(part => part.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
}

function splitLongSentence(value) {
    const text = String(value || '').trim()
    if (text.length <= 260) return [text]
    const protectedText = protectSentenceSplitAbbreviations(text)
    return protectedText
        .split(/(?<=\.)\s+/)
        .map(restoreSentenceSplitAbbreviations)
        .filter(Boolean)
}

function protectSentenceSplitAbbreviations(value) {
    return String(value || '')
        .replace(/\bPTE\./gi, match => match.replace('.', '<DOT>'))
        .replace(/\bLTD\./gi, match => match.replace('.', '<DOT>'))
        .replace(/\bNO\./gi, match => match.replace('.', '<DOT>'))
        .replace(/\bCO\./gi, match => match.replace('.', '<DOT>'))
        .replace(/\bINC\./gi, match => match.replace('.', '<DOT>'))
}

function restoreSentenceSplitAbbreviations(value) {
    return String(value || '').replace(/<DOT>/g, '.')
}

function capitalizeSentenceStart(value) {
    return String(value || '').replace(/^([a-z])/, (_, letter) => letter.toUpperCase())
}

function conciseHumanText(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim()
    if (text.length <= 260) return text

    const firstPoint = splitLongDetailText(text)[0] || text
    if (firstPoint.length <= 240) return `${firstPoint} More details available in the detailed results.`
    return `${firstPoint.slice(0, 237).trim()}... More details available in the detailed results.`
}

function countOccurrences(value, needle) {
    return String(value || '').split(needle).length - 1
}

function parseWhereToLook(whereText) {
    let value = String(whereText || '').trim()
    let preview = ''
    const previewMatch = value.match(/(?:Currently using|Preview):\s*"([\s\S]*)"$/)
    if (previewMatch) {
        preview = previewMatch[1].trim()
        value = value.slice(0, previewMatch.index).trim()
    }

    value = value.replace(/^Where to look:\s*/i, '').trim()
    value = value.replace(/\.$/, '').trim()

    return {
        location: value,
        preview
    }
}

function buildSourceNavigationEntries(result, reviewState, expectations = {}) {
    const items = [
        ...buildClientReadyActionItems(result, reviewState, { expectations }),
        ...buildWordingActionItems(result, reviewState)
    ]
    const entries = []
    const seen = new Set()

    for (const item of items) {
        for (const file of item.files || []) {
            const sourceLocations = file.sourceLocations?.length
                ? file.sourceLocations
                : (file.locations || []).map(location => ({ label: location }))
            for (const sourceLocation of sourceLocations) {
                const location = sourceLocation.label || ''
                if (!location || /location not available/i.test(location)) continue
                const key = `${item.id}:${file.fileId || file.fileName}:${location}:${sourceLocation.pageNumber || ''}`
                if (seen.has(key)) continue
                seen.add(key)
                entries.push({
                    itemId: item.id,
                    label: item.label,
                    fileId: file.fileId || '',
                    fileName: file.fileName || 'Source document',
                    location,
                    ...sourceLocation,
                    highlightTexts: sourceHighlightTexts(sourceLocation, item),
                    locationQuality: sourceLocationQuality(sourceLocation)
                })
            }
        }
    }

    return entries
}

function buildSourceViewerSource({ entry, localFiles, sourceFile = null, localOnly = true }) {
    if (!entry?.fileName && !entry?.fileId) return null
    const pageNumber = resolvePdfPageNumber(entry)
    const pageHash = pageNumber ? `#page=${pageNumber}` : ''
    const localMatches = sourceFile ? [] : (localFiles || []).filter(file => normalizeUploadName(file?.name) === normalizeUploadName(entry.fileName))
    if (!sourceFile && localMatches.length !== 1) return null
    const localFile = sourceFile || localMatches[0]

    if (localFile) {
        const blobUrl = window.URL.createObjectURL(localFile)
        return {
            ...entry,
            file: localFile,
            pageNumber,
            url: `${blobUrl}${pageHash}`,
            fullDocumentUrl: blobUrl,
            localOnly,
            blobUrl,
            isPdf: /\.pdf$/i.test(localFile.name || '') || localFile.type === 'application/pdf'
        }
    }

    return null
}

function sourceAccessMutationError(err, fallback) {
    const code = String(err?.response?.data?.code || '')
    if (code === 'QUALITY_REVIEW_SOURCE_OWNER_REQUIRED' || err?.response?.status === 403) {
        return 'Only the uploader can change or delete source file access.'
    }
    if (code === 'QUALITY_REVIEW_SOURCE_EXPIRED' || code === 'QUALITY_REVIEW_SOURCE_UNAVAILABLE' || err?.response?.status === 410) {
        return 'These source files are no longer available. The review results and page references remain.'
    }
    return err?.response?.data?.message || fallback
}

function downloadFromUrl(url, fileName) {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
}

function revokeSourceViewerBlob(source) {
    if (source?.blobUrl) {
        window.URL.revokeObjectURL(source.blobUrl)
    }
}

function parseMissedIssueLocation(value) {
    const text = String(value || '').trim()
    const page = text.match(/\bpage\s+(\d+)\b/i)?.[1] || ''
    const paragraph = text.match(/\bparagraph\s+(\d+)\b/i)?.[1] || ''
    const headingMatch = text.match(/\bheading(?:\s+["“]([^"”]+)["”])?/i)
    const sheet = text.match(/\b(?:worksheet|sheet)\s+["“]?([^,"”]+)["”]?/i)?.[1]?.trim() || ''
    const cell = text.match(/\bcell\s+([A-Z]+\d+)\b/i)?.[1] || ''

    return {
        page,
        paragraph,
        heading: headingMatch ? (headingMatch[1] || 'heading') : '',
        sheet,
        cell
    }
}

function formatSavedTime(value) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function buildReviewExpectationFields(review) {
    const source = review?.sourceOfTruth?.expectations || {}
    return {
        expectedCompanyName: source.expectedCompanyName || '',
        expectedUen: source.expectedUen || '',
        expectedRegisteredAddress: source.expectedRegisteredAddress || '',
        expectedFinancialPeriodStart: toDateInputValue(source.expectedFinancialPeriodStart || ''),
        expectedFinancialPeriodEnd: toDateInputValue(source.expectedFinancialPeriodEnd || source.expectedFye || ''),
        expectedFye: source.expectedFye || '',
        expectedYa: source.expectedYa || '',
        expectedTaxSignatory: '', // The saved-record privacy policy does not retain this personal field.
        expectedDirectors: expectationListText(source.expectedDirectors),
        expectedCompanySecretaries: expectationListText(source.expectedCompanySecretaries),
        expectedCountryOfDomicile: source.expectedCountryOfDomicile || ''
    }
}

function expectationListText(value) {
    if (Array.isArray(value)) return value.join('; ')
    return String(value || '')
}

function reviewWorkQueueStatus(review) {
    if (review?.lifecycle?.legacy || review?.compatibility?.legacy || review?.legacyReview || review?.persistence?.legacy) return 'legacy'
    if (['failed', 'cancelled'].includes(review?.execution?.status)) return 'failed'
    if (['queued', 'running'].includes(review?.execution?.status)) return 'processing'
    const blockers = review?.blockerSummary?.unresolved ?? review?.summary?.suspectedWrongClientFiles ?? 0
    if (blockers > 0 || review?.reviewOutcome === 'blocked') return 'blocked'
    const open = review?.resolutionSummary?.open ?? review?.summary?.actionItems ?? 0
    const clarification = review?.resolutionSummary?.needsClarification ?? 0
    if (open > 0 || clarification > 0 || review?.reviewOutcome === 'action_needed' || review?.reviewOutcome === 'manual_check') return 'open'
    return 'ready'
}

function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function createProgressId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function humanizeKey(value) {
    return String(value || '')
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

function chipColorForStatus(status) {
    switch (status) {
        case 'pass':
            return 'success'
        case 'warning':
            return 'warning'
        case 'fail':
            return 'error'
        default:
            return 'default'
    }
}

function alertSeverity(value) {
    if (value === 'error') return 'error'
    if (value === 'success') return 'success'
    return 'warning'
}

function humanizeConfidence(value) {
    if (value === 'high') return 'High'
    if (value === 'medium') return 'Medium'
    return 'Low'
}

function chipColorForWordingSeverity(value) {
    if (value === 'high') return 'error'
    if (value === 'medium') return 'warning'
    return 'default'
}

function humanizeSeverity(value) {
    if (value === 'high') return 'High'
    if (value === 'medium') return 'Medium'
    return 'Low'
}

function humanizeFindingCategory(value) {
    const map = {
        accounting_standard_alignment: 'Accounting standard',
        disclosure: 'Disclosure',
        grammar: 'Grammar',
        missing_information: 'Missing information',
        other: 'Other',
        presentation: 'Presentation',
        wording: 'Wording',
        terminology: 'Terminology',
        consistency: 'Consistency'
    }

    return map[value] || humanizeKey(value)
}

function statusLabel(status) {
    if (status === 'na') return 'Not applicable'
    const option = STATUS_OPTIONS.find(item => item.value === status)
    return option ? option.label : status
}

function staffFacingStatusLabel(status) {
    if (status === 'pass') return 'No issue'
    if (status === 'manual' || status === 'na') return 'Check manually'
    return 'Issue'
}

export default QualityReview
